<template>
  <div class="bg-gray-50">
    <div class="email-container flex bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style="height: calc(100vh - 150px);">
    <!-- 第一栏：文件夹和账户管理 -->
    <div class="w-72 bg-white border-r border-gray-200 flex flex-col">
      <!-- 顶部工具栏 -->
      <div class="p-4 border-b border-gray-200 space-y-3">
        <!-- 搜索框 -->
        <div class="relative">
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索邮件..."
            class="w-full pl-10 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
          />
      </div>
    </div>

      <!-- 文件夹列表 -->
      <div class="border-b border-gray-200">
        <!-- 文件夹列表内容 - 带折叠动画 -->
        <transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-96"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 max-h-96"
          leave-to-class="opacity-0 max-h-0"
        >
          <div v-show="!folderListCollapsed" class="overflow-hidden">
            <div class="p-2">
              <nav class="space-y-0.5">
                <button
                  v-for="folder in folders"
                  :key="folder.id"
                  @click="selectFolder(folder)"
                  :class="[
                    'w-full flex items-center px-3 h-8 text-sm rounded-lg transition-colors',
                    selectedFolder?.id === folder.id
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50',
                    { 'opacity-75 cursor-not-allowed': loadingFolderSelection && loadingFolderId === folder.id }
                  ]"
                  :disabled="loadingFolderSelection && loadingFolderId === folder.id"
                >
                  <!-- Loading状态或正常图标 -->
                  <div v-if="loadingFolderSelection && loadingFolderId === folder.id" class="h-4 w-4 mr-3 flex-shrink-0">
                    <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <component v-else :is="folder.icon" class="h-4 w-4 mr-3 flex-shrink-0" />
                  
                  <span class="flex-1 text-left">{{ folder.name }}</span>
                  <span v-if="folder.count" class="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full flex-shrink-0 min-w-[1.5rem] text-center">
                    {{ folder.count }}
                  </span>
                </button>
                
                <!-- 新建邮件按钮 -->
                <button
                  @click="openComposeModal"
                  class="w-full flex items-center px-3 h-8 text-sm rounded-lg transition-colors hover:bg-gray-50"
                >
                  <PlusIcon class="h-4 w-4 mr-3 flex-shrink-0" />
                  <span class="flex-1 text-left">新建邮件</span>
                </button>
              </nav>
            </div>
          </div>
        </transition>
        
        <!-- 折叠/展开按钮 -->
        <button
          @click="toggleFolderList"
          class="w-full flex items-center justify-center px-3 py-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
          :title="folderListCollapsed ? '展开聚合文件夹' : '收起聚合文件夹'"
        >
          <component :is="folderListCollapsed ? ChevronDownIcon : ChevronUpIcon" class="h-3.5 w-3.5 mr-1.5" />
          <span>{{ folderListCollapsed ? '展开聚合文件夹' : '收起聚合文件夹' }}</span>
        </button>
      </div>

      <!-- 邮件账户列表 -->
      <div class="flex-1 overflow-hidden flex flex-col">
        <div class="px-3 py-2 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <select
              v-model="accountSortMode"
              @change="handleAccountSortModeChange"
              class="h-8 w-[7.25rem] shrink-0 py-0 pl-2 pr-6 text-xs leading-8 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20"
              title="账户排序"
            >
              <option v-for="option in accountSortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <div class="relative flex-1 min-w-0">
              <input
                v-model="accountSearchQuery"
                type="text"
                placeholder="搜索邮件账户"
                class="w-full h-8 py-0 pl-7 pr-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20"
              />
              <MagnifyingGlassIcon class="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto custom-scrollbar">
          <div class="p-2 space-y-1.5">
            <!-- 邮件账户加载骨架 -->
            <div v-if="loadingAccounts" class="space-y-1.5">
              <div v-for="i in 4" :key="i" class="rounded-lg bg-white p-2.5 animate-pulse">
                <div class="flex items-center">
                  <div class="h-4 w-4 bg-gray-200 rounded mr-3"></div>
                  <div class="flex-1">
                    <div class="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 全部邮件账户选项 -->
            <div 
              v-else-if="!accountSearchQuery.trim()"
              @click="selectAllAccounts"
              :class="[
                'rounded-lg transition-colors cursor-pointer',
                selectedAccount === 'all' ? 'bg-blue-50' : 'bg-white hover:bg-gray-50',
                { 'opacity-75 cursor-not-allowed': loadingAccountSelection && loadingAccountId === 'all' }
              ]"
            >
              <div class="p-2.5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div v-if="loadingAccountSelection && loadingAccountId === 'all'" class="h-4 w-4 mr-3">
                      <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <EnvelopeIcon v-else class="h-4 w-4 mr-3 text-gray-600" />
                    <p class="text-sm font-medium text-gray-900">
                      全部邮件账户
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 邮件账户列表 -->
            <template v-if="!loadingAccounts">
            <div
              v-for="(account, accountIndex) in filteredEmailAccounts"
              :key="account.id"
              :class="[
                'rounded-lg transition-colors',
                selectedAccount?.id === account.id ? 'bg-blue-50' : 'bg-white hover:bg-gray-50',
                { 'opacity-75': loadingAccountSelection && loadingAccountId === account.id }
              ]"
            >
              <!-- 账户头部 -->
              <div
                @click="selectAccount(account)"
                :class="[
                  'p-2.5 transition-colors',
                  loadingAccountSelection && loadingAccountId === account.id ? 'cursor-not-allowed' : 'cursor-pointer'
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center flex-1 min-w-0">
                    <div v-if="loadingAccountSelection && loadingAccountId === account.id" class="h-4 w-4 mr-3 flex-shrink-0">
                      <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <button
                      v-else
                      @click.stop="syncOneAccount(account)"
                      :disabled="syncing"
                      class="h-4 w-4 mr-3 flex-shrink-0 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="同步该账户"
                    >
                      <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': syncingAccountId === account.id }" />
                    </button>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <p class="text-sm font-medium text-gray-900 truncate">
                          {{ account.name || account.email }}
                        </p>
                        <span 
                          v-if="account.unread_count > 0" 
                          @click.stop="confirmBatchMarkRead(account)"
                          class="flex-shrink-0 text-[8px] bg-red-500 text-white px-1.5 py-0.5 rounded-full font-semibold min-w-[1.5rem] text-center cursor-pointer hover:bg-red-600 hover:scale-110 transition-all"
                          :title="`点击将 ${account.unread_count} 封未读邮件全部标记为已读`"
                        >
                          {{ account.unread_count }}
                        </span>
                        <span 
                          v-if="account.notes" 
                          class="text-xs text-gray-400 truncate"
                          :title="account.notes"
                        >
                         {{ account.notes }}
                        </span>
                        <span
                          v-if="account.is_active === false"
                          class="flex-shrink-0 text-[9px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-medium"
                          title="该账户已停用，不会自动同步新邮件（历史邮件仍可查看）"
                        >
                          已停用
                        </span>
                      </div>
                      <p class="text-xs text-gray-500 truncate">{{ account.email }}</p>
                    </div>
                  </div>
                  
                  <div class="flex items-center flex-shrink-0">
                    <div
                      v-if="accountSortMode === 'custom' && !accountSearchQuery.trim()"
                      class="flex flex-col mr-0.5"
                    >
                      <button
                        type="button"
                        @click.stop="moveAccountInOrder(account, -1)"
                        :disabled="accountIndex === 0"
                        class="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="上移"
                      >
                        <ChevronUpIcon class="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        @click.stop="moveAccountInOrder(account, 1)"
                        :disabled="accountIndex === filteredEmailAccounts.length - 1"
                        class="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="下移"
                      >
                        <ChevronDownIcon class="h-3 w-3" />
                      </button>
                    </div>
                    <button
                      @click.stop="toggleAccount(account)"
                      :disabled="loadingAccountSelection && loadingAccountId === account.id"
                      class="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon 
                        v-if="!account.expanded"
                        class="h-4 w-4 text-gray-400"
                      />
                      <ChevronDownIcon 
                        v-else
                        class="h-4 w-4 text-gray-400"
                      />
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- 文件夹列表 (展开时显示) -->
              <div 
                v-if="account.expanded" 
                class="pb-1.5 px-2.5 border-t border-gray-100"
              >
                <div class="mt-1.5 space-y-0.5">
                  <div
                    v-for="folder in account.folders"
                    :key="folder.id"
                    :class="[
                      'w-full flex items-center px-2.5 h-8 text-sm rounded-md transition-colors',
                      selectedAccountFolder?.id === folder.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                      loadingAccountSelection && loadingAccountId === (folder.folder_type === 'user_created' ? `${account.id}-${folder.folder_type}-${folder.id}` : `${account.id}-${folder.folder_type}`)
                        ? 'opacity-75 cursor-not-allowed' 
                        : 'cursor-pointer'
                    ]"
                    @click="selectAccountFolder(account, folder)"
                  >
                    <!-- Loading图标或正常图标 -->
                    <div v-if="loadingAccountSelection && loadingAccountId === (folder.folder_type === 'user_created' ? `${account.id}-${folder.folder_type}-${folder.id}` : `${account.id}-${folder.folder_type}`)" class="h-3 w-3 mr-2 flex-shrink-0">
                      <svg class="animate-spin h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <component 
                      v-else
                      :is="folder.icon" 
                      class="h-3 w-3 mr-2 flex-shrink-0"
                    />
                    <!-- 文件夹名称和统计数字 -->
                    <div class="flex-1 flex items-center text-left">
                      <span class="truncate">{{ folder.name }}</span>
                      <span 
                        v-if="folder.count > 0" 
                        class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 ml-2 flex-shrink-0 min-w-[1.5rem] text-center"
                      >
                        {{ folder.count }}
                      </span>
                    </div>
                    
                    <!-- 删除按钮区域 -->
                    <div class="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                      <button
                        v-if="folder.folder_type === 'user_created'"
                        @click.stop="deleteFolder(account, folder)"
                        :disabled="loadingAccountSelection && loadingAccountId === `${account.id}-${folder.folder_type}-${folder.id}` || deletingFolderId === folder.id"
                        class="p-0.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        :title="deletingFolderId === folder.id ? '删除中...' : '删除文件夹'"
                      >
                        <svg v-if="deletingFolderId === folder.id" class="animate-spin h-3 w-3 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <TrashIcon v-else class="h-3 w-3 text-gray-600 hover:text-gray-800" />
                      </button>
                      <!-- 系统文件夹的占位符（不可见但占据空间） -->
                    </div>
                  </div>
    </div>

                <!-- 操作按钮组 - 横向三列 -->
                <div class="mt-1 border-t border-gray-100 pt-1.5 px-1">
                  <div class="grid grid-cols-3 gap-1">
                    <!-- 添加文件夹 -->
                    <button
                      @click.stop="addNewFolder(account)"
                      class="flex flex-col items-center justify-center px-2 py-1.5 text-xs rounded-md transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      title="添加文件夹"
                    >
                      <FolderIcon class="h-4 w-4 mb-0.5" />
                      <span class="text-[10px]">添加文件夹</span>
                    </button>
                    
                    <!-- 新建邮件 -->
                    <button
                      @click.stop="openComposeModalForAccount(account)"
                      class="flex flex-col items-center justify-center px-2 py-1.5 text-xs rounded-md transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      title="新建邮件"
                    >
                      <EnvelopeIcon class="h-4 w-4 mb-0.5" />
                      <span class="text-[10px]">新建邮件</span>
                    </button>
                    
                    <!-- 添加备注 -->
                    <button
                      @click.stop="openNotesModal(account)"
                      class="flex flex-col items-center justify-center px-2 py-1.5 text-xs rounded-md transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      title="添加备注"
                    >
                      <PencilSquareIcon class="h-4 w-4 mb-0.5" />
                      <span class="text-[10px]">添加备注</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            </template>
            
            <!-- 无搜索结果提示 -->
            <div v-if="!loadingAccounts && accountSearchQuery.trim() && filteredEmailAccounts.length === 0" class="p-4 text-center text-gray-500">
              <p class="text-sm">未找到匹配的邮件账户</p>
              <p class="text-xs mt-1">试试其他关键词</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 第二栏：邮件列表 -->
    <div class="w-96 bg-white border-r border-gray-200 flex flex-col">
      <!-- 邮件列表内容 -->
      <div class="flex-1 overflow-y-auto custom-scrollbar">
        <!-- 骨架加载状态 -->
        <div v-if="loadingEmails" class="flex flex-col h-full">
          <div class="flex-1 divide-y divide-gray-200">
            <div v-for="i in 8" :key="i" class="p-4">
              <div class="animate-pulse space-y-2">
                <div class="h-4 bg-gray-200 rounded"></div>
                <div class="h-3 bg-gray-200 rounded w-4/5"></div>
                <div class="flex justify-between items-center mt-2">
                  <div class="h-3 bg-gray-200 rounded w-1/3"></div>
                  <div class="flex space-x-1">
                    <div class="h-3 w-8 bg-gray-200 rounded"></div>
                    <div class="h-3 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 分页骨架 -->
          <div class="px-2 py-2 border-t border-gray-200">
            <div class="flex items-center justify-between animate-pulse">
              <div class="h-3 bg-gray-200 rounded w-12"></div>
              <div class="flex items-center space-x-1">
                <div class="h-5 w-5 bg-gray-200 rounded"></div>
                <div class="h-3 bg-gray-200 rounded w-10"></div>
                <div class="h-5 w-5 bg-gray-200 rounded"></div>
                <div class="h-5 w-8 bg-gray-200 rounded ml-1"></div>
                <div class="h-5 w-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="!loadingEmails && emailList.length === 0" class="p-8 text-center text-gray-500">
        <InboxIcon class="mx-auto h-12 w-12 text-gray-400" />
        <p class="mt-2">暂无邮件</p>
          <p class="text-sm">选择账户或文件夹查看邮件</p>
      </div>
        
        <!-- 邮件列表 -->
        <div v-else-if="!loadingEmails && emailList.length > 0" class="divide-y divide-gray-200">
          <div
            v-for="email in paginatedEmails"
            :key="email.id"
            @click="selectEmail(email)"
            :class="[
              'p-4 cursor-pointer transition-colors relative',
              selectedEmail?.id === email.id ? 'bg-blue-50' : 'hover:bg-gray-50',
              !email.is_read ? 'bg-blue-25' : '',
              { 'opacity-50 pointer-events-none': loadingEmailId && loadingEmailId !== email.id }
            ]"
          >
            <!-- 加载状态覆盖层 -->
            <div v-if="loadingEmailId === email.id" class="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center">
              <div class="animate-spin h-5 w-5 text-blue-600">
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
    </div>
  </div>
            <div class="space-y-2">
              <!-- 上方标题 -->
              <p :class="[
                'text-sm truncate font-medium',
                !email.is_read ? 'text-gray-900' : 'text-gray-800'
              ]">
                {{ email.subject }}
              </p>
              
              <!-- 中间发件人信息和文件夹 -->
              <div class="flex items-center justify-between text-xs text-gray-600">
                <!-- 发件人信息 (可截断) -->
                <span class="truncate mr-2">
                  {{ getContactDisplayText(email) }}
                </span>
                <!-- 文件夹名称 (固定显示) -->
                <span class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600" style="font-size: 11px;">
                  {{ translateFolderName(email.folder_name) }}
                </span>
              </div>
              
              <!-- 下方分组、标签和时间 -->
              <div class="flex items-center justify-between">
                <div class="flex flex-wrap items-center gap-1 min-w-0 flex-1 mr-2">
                  <span 
                    v-if="email.customer_group_name" 
                    class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-white" 
                    style="font-size: 10px;"
                    :style="{ backgroundColor: email.customer_group_color || '#3B82F6' }"
                    :title="'客户分组: ' + email.customer_group_name"
                  >
                    {{ email.customer_group_name }}
                  </span>
                  <span
                    v-for="(tag, tagIdx) in (email.customer_tags || [])"
                    :key="'tag-' + tagIdx"
                    class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded"
                    style="font-size: 10px;"
                    :style="{ backgroundColor: tag.background_color || '#3b82f6', color: tag.text_color || '#ffffff' }"
                    :title="'标签: ' + tag.name"
                  >
                    {{ tag.name }}
                  </span>
                  <span v-if="email.has_attachments" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600" style="font-size: 11px;">
                    附件
                  </span>
                  <span v-if="email.is_important" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-500 text-white" style="font-size: 11px;">
                    重要
                  </span>
                  <span v-if="!email.is_read" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700" style="font-size: 11px;">
                    未读
                  </span>
                  <span
                    v-if="hasOutboundTracking(email)"
                    :class="getOutboundTrackingBadgeClass(email)"
                    class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                    style="font-size: 11px;"
                    :title="getOutboundTrackingTitle(email)"
                  >
                    {{ getOutboundTrackingLabel(email) }}
                  </span>
                </div>
                <span class="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">
                  {{ formatDateTime(email.received_date) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页控件 -->
      <div v-if="!loadingEmails && emailList.length > 0" class="px-2 py-2 border-t border-gray-200">
        <div class="flex items-center justify-between text-xs">
          <div class="flex items-center space-x-1.5 flex-shrink-0">
            <span class="text-gray-500 whitespace-nowrap">共 {{ totalEmails }} 封</span>
            <select
              v-model="pageSize"
              @change="handlePageSizeChange"
              :disabled="loadingEmails"
              class="w-14 min-w-[3.5rem] flex-shrink-0 pl-1.5 pr-6 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
            <span class="text-gray-500 whitespace-nowrap">/页</span>
          </div>
          <div class="flex items-center space-x-0.5">
            <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1 || loadingEmails"
              class="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeftIcon class="h-3 w-3" />
            </button>
            <span class="px-1 text-gray-700 whitespace-nowrap">{{ currentPage }}/{{ totalPages }}</span>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage >= totalPages || loadingEmails"
              class="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRightIcon class="h-3 w-3" />
            </button>
            <input
              v-model.number="jumpToPage"
              type="number"
              min="1"
              :max="totalPages"
              @keyup.enter="handleJumpToPage"
              class="w-10 ml-1 px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="页"
            />
            <button
              @click="handleJumpToPage"
              :disabled="loadingEmails"
              class="px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              GO
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 第三栏：邮件内容 -->
    <div class="w-0 flex-1 bg-white border-r border-gray-200 flex flex-col">
      <!-- 骨架加载状态 -->
      <div v-if="loadingEmailContent" class="flex-1">
        <div class="animate-pulse p-4 space-y-4">
          <!-- 头部信息骨架 -->
          <div class="border-b border-gray-200 pb-4">
            <!-- 标题骨架 -->
            <div class="h-6 bg-gray-200 rounded mb-3 w-3/4"></div>
            <!-- 按钮骨架 -->
            <div class="flex items-center space-x-3 mb-4">
              <div class="h-7 w-7 bg-gray-200 rounded"></div>
              <div class="h-7 w-7 bg-gray-200 rounded"></div>
              <div class="h-7 w-7 bg-gray-200 rounded"></div>
              <div class="h-7 w-7 bg-gray-200 rounded"></div>
              <div class="h-6 w-12 bg-gray-200 rounded"></div>
            </div>
            <!-- 分割线骨架 -->
            <div class="border-t border-gray-200 mb-4 -mx-4"></div>
            <!-- 详细信息骨架 -->
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <div class="h-3 bg-gray-200 rounded w-2/3"></div>
                <div class="h-3 w-3 bg-gray-200 rounded"></div>
              </div>
              <div class="flex items-center justify-between">
                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                <div class="h-3 w-3 bg-gray-200 rounded"></div>
              </div>
              <div class="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
          <!-- 内容区域骨架 -->
          <div class="p-4 space-y-3">
            <div class="h-3 bg-gray-200 rounded"></div>
            <div class="h-3 bg-gray-200 rounded w-5/6"></div>
            <div class="h-3 bg-gray-200 rounded w-4/5"></div>
            <div class="h-3 bg-gray-200 rounded w-full"></div>
            <div class="h-3 bg-gray-200 rounded w-3/4"></div>
            <div class="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-else-if="!loadingEmailContent && !selectedEmail" class="flex-1 flex items-center justify-center text-gray-500">
        <div class="text-center">
          <EnvelopeIcon class="mx-auto h-12 w-12 text-gray-400" />
          <p class="mt-2">选择邮件查看内容</p>
        </div>
      </div>
      
      <!-- 邮件内容 -->
      <div v-else-if="!loadingEmailContent && selectedEmail" class="flex-1 flex flex-col min-h-0">
        <!-- 邮件头部信息 -->
        <div class="flex-shrink-0 p-4">
          <!-- 邮件标题 -->
          <h2 class="text-xl font-medium text-gray-900 mb-3">{{ selectedEmail.subject }}</h2>
          
          <!-- 操作按钮 -->
          <div class="flex items-center space-x-3 mb-4">
            <button 
              @click="openReplyModal(selectedEmail)" 
              class="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded" 
              title="回复邮件"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button 
              @click="toggleEmailImportant(selectedEmail)"
              :class="selectedEmail.is_important 
                ? 'p-1.5 text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded' 
                : 'p-1.5 text-gray-400 bg-gray-50 hover:bg-orange-50 hover:text-orange-600 rounded'"
              :title="selectedEmail.is_important ? '取消重要标记' : '标记为重要'">
              <StarIcon class="h-4 w-4" :class="selectedEmail.is_important ? 'fill-current' : ''" />
            </button>
            <button 
              @click="toggleEmailRead(selectedEmail)"
              :class="selectedEmail.is_read 
                ? 'p-1.5 text-gray-400 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded' 
                : 'p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded'"
              :title="selectedEmail.is_read ? '标记为未读' : '标记为已读'">
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            <button 
              @click="openArchiveModal"
              class="p-1.5 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded" 
              title="归档邮件">
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </button>
            <button 
              @click="handleTranslateEmail"
              :disabled="translatingEmail"
              :class="showTranslation 
                ? 'p-1.5 text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50' 
                : 'p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50'"
              :title="showTranslation ? '显示原文' : '翻译邮件内容'">
              <svg v-if="translatingEmail" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </button>
            <button 
              @click="refetchEmailContent(selectedEmail)"
              :disabled="refetchingContent"
              class="p-1.5 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded disabled:opacity-50 disabled:cursor-not-allowed" 
              title="重新获取邮件内容">
              <svg v-if="refetchingContent" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button 
              @click="openHistoryModal"
              class="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200" 
              title="查看历史往来邮件">
              历史往来
            </button>
            <!-- 移动到收件箱按钮 - 仅在垃圾邮件文件夹显示 -->
            <button 
              v-if="selectedEmail.folder_type === 'spam'"
              @click="moveEmailToInbox(selectedEmail)"
              :disabled="movingToInbox"
              class="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1" 
              title="移动到收件箱">
              <svg v-if="movingToInbox" class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{{ movingToInbox ? '移动中...' : '移到收件箱' }}</span>
            </button>
            <div class="flex items-center space-x-2">
              <span :class="getFolderStatusStyle(selectedEmail.folder_type)" class="px-2 py-1 text-xs rounded">
                {{ getFolderStatusText(selectedEmail.folder_type) }}
              </span>
              <span
                v-if="hasOutboundTracking(selectedEmail)"
                :class="getOutboundTrackingBadgeClass(selectedEmail)"
                class="px-2 py-1 text-xs rounded"
                :title="getOutboundTrackingTitle(selectedEmail)"
              >
                {{ getOutboundTrackingLabel(selectedEmail) }}
              </span>
            </div>
          </div>
          
          <!-- 分割线和展开详情按钮 -->
          <div class="relative mb-4 -mx-4">
            <div class="border-t border-gray-200"></div>
            <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <button 
                @click="showEmailDetails = !showEmailDetails"
                class="px-2 bg-white text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {{ showEmailDetails ? '收起详情 ▲' : '展开详情 ▼' }}
              </button>
            </div>
          </div>
          
          <!-- 邮件详细信息 -->
          <div v-show="showEmailDetails" class="space-y-2 mb-4 pb-4 border-b border-gray-200 -mx-4 px-4">
            <div class="flex items-center justify-between text-xs text-gray-600">
              <div class="flex items-center">
                <span class="font-medium mr-1">发件人:</span>
                <span>{{ selectedEmail.sender_name }} &lt;{{ selectedEmail.sender_email }}&gt;</span>
              </div>
              <button 
                @click="copyToClipboard(selectedEmail.sender_email)"
                class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="复制发件人邮箱"
              >
                <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            <div class="flex items-center justify-between text-xs text-gray-600">
              <div class="flex items-center">
                <span class="font-medium mr-1">收件人:</span>
                <span>{{ selectedEmail.recipient_list }}</span>
              </div>
              <button 
                @click="copyToClipboard(selectedEmail.recipient_list)"
                class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="复制收件人邮箱"
              >
                <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            <div class="flex items-center text-xs text-gray-600">
              <span class="font-medium mr-1">时间:</span>
              <span>{{ formatDateTime(selectedEmail.received_date) }}</span>
            </div>
            <!-- 出站邮件打开追踪 -->
            <OutboundTrackingPanel :email="selectedEmail" />
          </div>
          
          <!-- 附件区域 - 新位置 -->
          <div v-if="nonInlineAttachments.length > 0" class="mb-4">
            
            <!-- 3列网格附件展示 -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <div
                v-for="(attachment, index) in (showAllAttachments ? nonInlineAttachments : nonInlineAttachments.slice(0, 3))"
                :key="attachment.id"
                class="flex items-center p-2 bg-gray-50 rounded border border-gray-200 hover:shadow-sm transition-all"
              >
                <!-- 文件图标 -->
                <div class="flex-shrink-0 mr-3">
                  <svg v-if="getFileTypeFromContentType(attachment.content_type) === 'pdf'" class="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'image'" class="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'video'" class="h-10 w-10 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'excel'" class="h-10 w-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'word'" class="h-10 w-10 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                  </svg>
                  <DocumentTextIcon v-else class="h-10 w-10 text-gray-500" />
                </div>
                
                <!-- 文件信息 -->
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate" :title="attachment.filename || attachment.name">
                    {{ attachment.filename || attachment.name }}
                  </div>
                  <div class="flex items-center gap-2 mt-0.5">
                    <span class="text-xs text-gray-500">
                      {{ formatFileSize(attachment.size) }}
                    </span>
                    <button
                      v-if="canPreview(attachment.content_type)"
                      @click="previewFile(attachment)"
                      class="text-xs text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      预览
                    </button>
                    <button
                      @click="downloadAttachment(attachment)"
                      :disabled="downloadingAttachments.has(attachment.id)"
                      class="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
                    >
                      {{ downloadingAttachments.has(attachment.id) ? '下载中...' : '保存' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 展开更多按钮 -->
            <div v-if="nonInlineAttachments.length > 3" class="text-center mt-2">
              <button 
                @click="showAllAttachments = !showAllAttachments"
                class="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {{ showAllAttachments ? '收起 ▲' : `展开更多 (${nonInlineAttachments.length - 3}) ▼` }}
              </button>
            </div>
            
            <!-- 底部分割线 -->
            <div class="border-b border-gray-200 mt-4 -mx-4"></div>
          </div>
        </div>

        <!-- 翻译提示条 -->
        <div v-if="showTranslation" class="flex-shrink-0 px-4 py-2 bg-green-50 border-b border-green-200 flex items-center justify-between">
          <span class="text-sm text-green-700">已翻译为中文</span>
          <button @click="showTranslation = false" class="text-xs text-green-600 hover:text-green-800 underline">显示原文</button>
        </div>
        
        <!-- 邮件内容 -->
        <div class="flex-1 overflow-y-auto custom-scrollbar px-4 pb-4 min-h-0">
          <IsolatedEmailContent 
            :html-content="showTranslation && translatedHtml ? translatedHtml : selectedEmail.body_html"
            :text-content="selectedEmail.body_text"
          />
        </div>
      </div>
    </div>

    <!-- 第四栏：客户信息 -->
    <div class="w-80 bg-white flex flex-col">
      <div class="flex-shrink-0 p-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">客户信息</h3>
      </div>
      
      <!-- 表单内容区域 - 可滚动 -->
      <div class="flex-1 overflow-y-auto custom-scrollbar min-h-0 relative">
        <!-- 客户信息骨架加载状态 -->
        <div v-if="loadingCustomer" class="p-4">
          <div class="animate-pulse space-y-4">
            <!-- 邮箱地址骨架 -->
            <div>
              <div class="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div class="h-9 bg-gray-200 rounded"></div>
              <div class="h-6 bg-gray-200 rounded w-16 mt-1"></div>
            </div>
            
            <!-- 姓名骨架 -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
                <div class="h-9 bg-gray-200 rounded"></div>
              </div>
              <div>
                <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
                <div class="h-9 bg-gray-200 rounded"></div>
              </div>
            </div>
            
            <!-- 其他字段骨架 -->
            <div>
              <div class="h-4 bg-gray-200 rounded w-12 mb-2"></div>
              <div class="h-9 bg-gray-200 rounded"></div>
            </div>
            
            <div>
              <div class="h-4 bg-gray-200 rounded w-16 mb-2"></div>
              <div class="h-9 bg-gray-200 rounded"></div>
            </div>
            
            <!-- 标签骨架 -->
            <div>
              <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
              <div class="flex flex-wrap gap-1.5">
                <div class="h-6 bg-gray-200 rounded w-12"></div>
                <div class="h-6 bg-gray-200 rounded w-16"></div>
                <div class="h-6 bg-gray-200 rounded w-14"></div>
              </div>
            </div>
            
            <!-- 分组骨架 -->
            <div>
              <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
              <div class="flex flex-wrap gap-1.5">
                <div class="h-6 bg-gray-200 rounded w-16"></div>
                <div class="h-6 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
            
            <!-- 地址信息骨架 -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
                <div class="h-9 bg-gray-200 rounded"></div>
              </div>
              <div>
                <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
                <div class="h-9 bg-gray-200 rounded"></div>
              </div>
            </div>
            
            <!-- 备注骨架 -->
            <div>
              <div class="h-4 bg-gray-200 rounded w-8 mb-2"></div>
              <div class="h-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        
        <!-- 无邮件选择提示 -->
        <div v-else-if="!selectedEmail" class="absolute inset-0 flex items-center justify-center text-gray-500">
          <div class="text-center">
            <EnvelopeIcon class="mx-auto h-12 w-12 text-gray-400" />
            <p class="mt-2">请选择邮件以查看客户信息</p>
          </div>
        </div>
        
        <!-- 客户信息表单 -->
        <div v-else class="p-4 space-y-4">
          <!-- 邮箱地址 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              邮箱地址 <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                v-model="customerInfo.email"
                type="email"
                required
                readonly
                class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                placeholder="客户的邮箱地址"
              />
              <div v-if="loadingCustomer" class="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <!-- 客户状态提示 -->
            <div class="mt-1">
              <span v-if="customerExists" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <svg class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                现有客户
              </span>
              <span v-else-if="customerInfo.email && !loadingCustomer" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                <svg class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                新客户
              </span>
            </div>
          </div>
          
          <!-- 名字和姓氏 -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">名字</label>
              <input
                v-model="customerInfo.firstName"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="First Name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">姓氏</label>
              <input
                v-model="customerInfo.lastName"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Last Name"
              />
            </div>
          </div>
          
          <!-- 电话和客户来源 -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">电话</label>
              <input
                v-model="customerInfo.phone"
                type="tel"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="联系电话"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">归属邮箱</label>
              <input
                v-model="customerInfo.ownedEmailAccount"
                type="email"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="客户归属的邮箱地址"
              />
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">客户来源</label>
              <input
                v-model="customerInfo.source"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="网站、推荐等"
              />
            </div>
          
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">订单号</label>
              <input
                v-model="customerInfo.orderNumber"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="订单编号"
              />
            </div>
          </div>
          
          <!-- 标签选择 -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-gray-700">标签</label>
              <div class="relative w-32">
                <input
                  v-model="tagSearchQuery"
                  type="text"
                  placeholder="搜索标签"
                  class="w-full h-6 pl-5 pr-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20"
                />
                <MagnifyingGlassIcon class="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div v-if="loadingTags" class="flex items-center justify-center py-4">
              <svg class="animate-spin h-5 w-5 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm text-gray-600">加载标签中...</span>
            </div>
            <div v-else-if="filteredTags.length === 0 && availableTags.length === 0" class="text-sm text-gray-500 py-2">
              暂无可用标签
            </div>
            <div v-else-if="filteredTags.length === 0 && tagSearchQuery.trim()" class="text-sm text-gray-500 py-2">
              未找到匹配的标签
            </div>
            <div v-else class="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto custom-scrollbar p-1">
              <label v-for="tag in filteredTags" :key="tag.id" class="cursor-pointer">
                <input 
                  type="checkbox" 
                  :value="tag.id"
                  v-model="customerInfo.tagIds"
                  class="sr-only" 
                />
                <span 
                  class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full transition-colors"
                  :class="customerInfo.tagIds.includes(tag.id) 
                    ? 'bg-blue-100 text-blue-800 border border-transparent' 
                    : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                  :style="customerInfo.tagIds.includes(tag.id) 
                    ? { backgroundColor: tag.background_color || '#DBEAFE', color: tag.text_color || '#1E40AF' } 
                    : {}"
                >
                  {{ tag.name }}
                </span>
              </label>
            </div>
          </div>
          
          <!-- 分组选择 -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-gray-700">
              分组 <span class="text-red-500">*</span>
            </label>
              <div class="relative w-32">
                <input
                  v-model="groupSearchQuery"
                  type="text"
                  placeholder="搜索分组"
                  class="w-full h-6 pl-5 pr-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20"
                />
                <MagnifyingGlassIcon class="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div v-if="loadingGroups" class="flex items-center justify-center py-4">
              <svg class="animate-spin h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm text-gray-600">加载分组中...</span>
            </div>
            <div v-else-if="filteredGroups.length === 0 && availableGroups.length === 0" class="text-sm text-gray-500 py-2">
              暂无可用分组
            </div>
            <div v-else-if="filteredGroups.length === 0 && groupSearchQuery.trim()" class="text-sm text-gray-500 py-2">
              未找到匹配的分组
            </div>
            <div v-else class="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto custom-scrollbar p-1">
              <label v-for="group in filteredGroups" :key="group.id" class="cursor-pointer">
                <input 
                  type="checkbox" 
                  :value="group.id"
                  v-model="customerInfo.groupIds"
                  class="sr-only" 
                />
                <span 
                  class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full transition-colors"
                  :class="customerInfo.groupIds.includes(group.id) 
                    ? 'bg-green-100 text-green-800 border border-green-300' 
                    : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                >
                  {{ group.name }}
                  <span v-if="group.description" class="ml-1 text-gray-500">· {{ group.description }}</span>
                </span>
              </label>
            </div>
            <p v-if="!loadingGroups && availableGroups.length > 0 && customerInfo.groupIds.length === 0" class="text-xs text-red-500 mt-1">请至少选择一个分组</p>
          </div>
          
          <!-- 产品搜索 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">搜索产品</label>
            <div class="relative">
              <input
                v-model="productSearchQuery"
                @input="searchProducts($event.target.value)"
                type="text"
                class="w-full px-3 py-2 pl-8 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="输入产品名称或编号..."
              />
              <div class="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <svg class="h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div v-if="isSearchingProducts" class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                <svg class="animate-spin h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              
              <!-- 搜索结果 -->
              <div v-if="productSearchResults.length > 0" class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                <div 
                  v-for="product in productSearchResults" 
                  :key="product.id" 
                  @click="addProduct(product)"
                  class="flex items-center justify-between p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 text-xs truncate">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">
                      <span class="font-mono">{{ product.code }}</span>
                      <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
                    </div>
                  </div>
                  <button class="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    添加
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 已绑定产品列表 -->
          <div v-if="boundProducts.length > 0">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                已绑定产品
              </label>
              <span class="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {{ boundProducts.length }}
              </span>
            </div>
            
            <div class="space-y-1 max-h-24 overflow-y-auto custom-scrollbar">
              <div 
                v-for="product in boundProducts" 
                :key="product.id"
                class="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">{{ product.name }}</div>
                  <div class="text-xs text-gray-500">
                    <span class="font-mono">{{ product.code }}</span>
                    <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
                  </div>
                </div>
                <button 
                  @click="removeProduct(product)"
                  class="ml-2 p-0.5 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                  title="移除产品"
                >
                  <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- 地址信息 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">地址信息</label>
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="customerInfo.country"
                  type="text"
                  placeholder="国家"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  v-model="customerInfo.state"
                  type="text"
                  placeholder="省/州"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="customerInfo.city"
                  type="text"
                  placeholder="城市"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  v-model="customerInfo.postalCode"
                  type="text"
                  placeholder="邮政编码"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <textarea
                v-model="customerInfo.address"
                rows="2"
                placeholder="详细地址"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              ></textarea>
            </div>
          </div>
          
          <!-- 备注 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">备注</label>
            <textarea
              v-model="customerInfo.notes"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              placeholder="备注信息"
            ></textarea>
          </div>
        </div>
      </div>
      
      <!-- 操作按钮区域骨架 -->
      <div v-if="loadingCustomer" class="flex-shrink-0 border-t border-gray-200 p-4">
        <div class="animate-pulse">
          <div class="h-9 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
      
      <!-- 操作按钮区域 - 固定在底部 -->
      <div v-else-if="selectedEmail" class="flex-shrink-0 border-t border-gray-200 p-4">
        <!-- 保存新客户按钮 -->
        <button
          v-if="!customerExists && customerInfo.email"
          @click="saveCustomerInfo"
          :disabled="loadingCustomer || savingCustomer"
          class="w-full bg-green-600 text-white border border-green-600 rounded px-2 py-1.5 text-sm hover:bg-green-700 hover:border-green-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          style="min-height: 34px;"
        >
          <svg v-if="savingCustomer" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <UserPlusIcon v-else class="h-4 w-4 mr-2" />
          {{ savingCustomer ? '保存中...' : '保存新客户' }}
        </button>
        
        <!-- 编辑现有客户按钮 -->
        <button
          v-else-if="customerExists"
          @click="editCustomerInfo"
          :disabled="loadingCustomer || savingCustomer"
          class="w-full bg-blue-600 text-white border border-blue-600 rounded px-2 py-1.5 text-sm hover:bg-blue-700 hover:border-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          style="min-height: 34px;"
        >
          <svg v-if="savingCustomer" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {{ savingCustomer ? '更新中...' : '更新客户信息' }}
        </button>
        
      </div>
    </div>

    </div>
  </div>
  
  <!-- 文件预览组件 -->
  <FilePreview
    :key="previewedFile?.id || 'preview'"
    :visible="showFilePreview"
    :attachment="previewedFile"
    @close="closeFilePreview"
    @download="downloadAttachment"
    @url-resolved="onAttachmentUrlResolved"
  />

  <!-- 归档模态框 -->
  <div v-if="showArchiveModal" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="showArchiveModal"
        class="fixed inset-0 transition-opacity"
        @click="closeArchiveModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            邮件归档
          </h3>
          <button
            @click="closeArchiveModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- 加载状态 -->
        <div v-if="loadingArchives" class="flex items-center justify-center py-12">
          <svg class="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="ml-3 text-sm text-gray-600">加载中...</span>
        </div>
        
        <!-- 主要内容 -->
        <div v-else class="space-y-6">
          <!-- 已归档文件夹 -->
          <div v-if="availableArchiveFolders.filter(f => f.isArchived).length > 0" class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">已归档文件夹</label>
            <div class="border-green-200 rounded-lg">
              <div class="space-y-3">
                <div 
                  v-for="folder in availableArchiveFolders.filter(f => f.isArchived)"
                  :key="'archived-' + folder.id"
                  class="flex items-center justify-between p-3 bg-white border border-green-200 rounded-lg"
                >
                  <div class="flex items-center">
                    <svg class="h-5 w-5 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span class="text-sm font-medium text-gray-900">{{ folder.displayName }}</span>
                  </div>
                  <button
                    @click="executeUnarchive(folder)"
                    :disabled="unarchivingFromFolder === folder.id"
                    class="inline-flex items-center px-3 py-0.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg v-if="unarchivingFromFolder === folder.id" class="animate-spin h-3 w-3 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    取消归档
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 可归档文件夹 -->
          <div v-if="availableArchiveFolders.filter(f => !f.isArchived).length > 0" class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">选择归档文件夹</label>
            <div class="space-y-2 max-h-64 overflow-y-auto">
              <div 
                v-for="folder in availableArchiveFolders.filter(f => !f.isArchived)"
                :key="'available-' + folder.id"
                @click="selectArchiveFolder(folder)"
                :class="[
                  'flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all',
                  selectedArchiveFolder?.id === folder.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                ]"
              >
                <svg class="h-5 w-5 text-gray-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                </svg>
                <span class="text-sm font-medium text-gray-900">{{ folder.displayName }}</span>
                <svg v-if="selectedArchiveFolder?.id === folder.id" class="h-4 w-4 text-blue-500 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- 无文件夹提示 -->
          <div v-if="availableArchiveFolders.length === 0" class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
            </svg>
            <p class="text-sm text-gray-500">暂无可用的自定义文件夹</p>
            <p class="text-xs text-gray-400 mt-1">请先创建自定义文件夹</p>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
          <button
            @click="closeArchiveModal"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
            :disabled="archivingToFolder"
          >
            关闭
          </button>
          
          <button 
            v-if="availableArchiveFolders.filter(f => !f.isArchived).length > 0"
            @click="executeArchive"
            type="button"
            :disabled="!selectedArchiveFolder || loadingArchives || archivingToFolder"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            <svg v-if="archivingToFolder" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ archivingToFolder ? '归档中...' : '开始归档' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 回复邮件模态框 -->
  <EmailReplyModal
    :show-modal="showReplyModal"
    :original-email="replyTargetEmail"
    :is-reply="true"
    @close="closeReplyModal"
    @sent="onEmailSent"
  />
  
  <!-- 新建邮件模态框 -->
  <EmailReplyModal
    :show-modal="showComposeModal"
    :original-email="null"
    :is-reply="false"
    :default-from-account-id="defaultFromAccountId"
    @close="closeComposeModal"
    @sent="onEmailSent"
  />
  
  <!-- 添加备注模态框 -->
  <div v-if="showNotesModal" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" @click="closeNotesModal"></div>
      
      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <PencilSquareIcon class="h-6 w-6 text-blue-600" />
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                添加备注
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500 mb-3">
                  为 <span class="font-medium text-gray-700">{{ currentNotesAccount?.email }}</span> 添加备注信息
                </p>
                <textarea
                  v-model="accountNotes"
                  rows="6"
                  class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="输入备注内容..."
                ></textarea>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            @click="saveNotes"
            :disabled="savingNotes"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="savingNotes" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ savingNotes ? '保存中...' : '保存' }}
          </button>
          <button
            type="button"
            @click="closeNotesModal"
            :disabled="savingNotes"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- 批量已读确认弹窗 -->
  <div v-if="showBatchReadConfirm" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showBatchReadConfirm = false"></div>
      <div class="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <EnvelopeOpenIcon class="h-5 w-5 text-blue-600" />
          </div>
          <h3 class="ml-3 text-lg font-medium text-gray-900">批量标记已读</h3>
        </div>
        <p class="text-sm text-gray-600 mb-6">
          确定将 <span class="font-semibold text-gray-900">{{ batchReadAccount?.name || batchReadAccount?.email }}</span> 
          的 <span class="font-semibold text-red-500">{{ batchReadAccount?.unread_count }}</span> 封未读邮件全部标记为已读？
        </p>
        <div class="flex justify-end space-x-3">
          <button
            @click="showBatchReadConfirm = false"
            :disabled="batchReadLoading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            @click="executeBatchMarkRead"
            :disabled="batchReadLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <svg v-if="batchReadLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ batchReadLoading ? '处理中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 历史往来模态框 -->
  <div v-if="showHistoryModal" class="fixed inset-0 z-50 overflow-hidden">
    <!-- 背景遮罩 -->
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeHistoryModal"></div>
    
    <!-- 模态框容器 -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="relative bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col">
        
        <!-- 头部 -->
        <div class="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-base font-medium text-gray-900">
            与 {{ historyContactEmail }} 的往来邮件
          </h2>
          <button @click="closeHistoryModal" class="text-gray-400 hover:text-gray-600">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- 主体区域 -->
        <div class="flex-1 flex min-h-0">
          
          <!-- 左侧邮件列表 -->
          <div class="w-[30%] border-r border-gray-200 flex flex-col">
            <!-- Tab栏 -->
            <div class="flex-shrink-0 flex border-b border-gray-200">
              <button 
                @click="switchHistoryTab('all')"
                :class="historyActiveTab === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'"
                class="flex-1 px-4 py-3 text-sm font-medium transition-colors"
              >
                全部 ({{ historyTabCounts.all }})
              </button>
              <button 
                @click="switchHistoryTab('received')"
                :class="historyActiveTab === 'received' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'"
                class="flex-1 px-4 py-3 text-sm font-medium transition-colors"
              >
                收到 ({{ historyTabCounts.received }})
              </button>
              <button 
                @click="switchHistoryTab('sent')"
                :class="historyActiveTab === 'sent' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'"
                class="flex-1 px-4 py-3 text-sm font-medium transition-colors"
              >
                发出 ({{ historyTabCounts.sent }})
              </button>
            </div>
            
            <!-- 邮件列表 -->
            <div class="flex-1 overflow-y-auto">
              <div v-if="loadingHistory" class="p-8 text-center">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p class="mt-2 text-sm text-gray-500">加载中...</p>
              </div>
              <div v-else-if="historyEmailList.length === 0" class="p-8 text-center text-gray-400">
                <svg class="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p class="mt-2 text-sm">暂无往来邮件</p>
              </div>
              <div v-else>
                <div 
                  v-for="email in historyEmailList" 
                  :key="email.id"
                  @click="selectHistoryEmail(email)"
                  :class="historySelectedEmail?.id === email.id ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50 border-l-4 border-transparent'"
                  class="p-3 cursor-pointer border-b border-gray-100 transition-colors"
                >
                  <p class="text-sm font-medium text-gray-900 truncate mb-1">
                    {{ email.subject || '(无主题)' }}
                  </p>
                  <p class="text-xs text-gray-400 mb-1">{{ email.sender_name }} &lt;{{ email.sender_email }}&gt;</p>
                  <p class="text-xs text-gray-500 flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span :class="email.folder_type === 'inbox' ? 'bg-green-100 text-green-800' : email.folder_type === 'sent' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'" class="inline-block px-2 py-0.5 rounded">
                        {{ translateFolderName(email.folder_name) }}
                      </span>
                      <!-- 已发送邮件不显示已读/未读状态 -->
                      <template v-if="email.folder_type !== 'sent'">
                        <span v-if="!email.is_read" class="text-red-600 font-semibold">未读</span>
                        <span v-else class="text-gray-400">已读</span>
                      </template>
                      <span
                        v-if="hasOutboundTracking(email)"
                        :class="getOutboundTrackingBadgeClass(email)"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                        :title="getOutboundTrackingTitle(email)"
                      >
                        {{ getOutboundTrackingLabel(email) }}
                      </span>
                    </div>
                    <span class="text-gray-400">{{ formatDateTime(email.received_date) }}</span>
                  </p>
                </div>
              </div>
            </div>
            
            <!-- 分页 -->
            <div v-if="historyPagination.totalPages > 1" class="flex-shrink-0 p-4 border-t border-gray-200 flex items-center justify-between">
              <button 
                @click="goToHistoryPage(historyPagination.currentPage - 1)"
                :disabled="historyPagination.currentPage <= 1"
                class="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← 上一页
              </button>
              <span class="text-sm text-gray-600">
                第 {{ historyPagination.currentPage }}/{{ historyPagination.totalPages }} 页
              </span>
              <button 
                @click="goToHistoryPage(historyPagination.currentPage + 1)"
                :disabled="historyPagination.currentPage >= historyPagination.totalPages"
                class="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页 →
              </button>
            </div>
          </div>
          
          <!-- 右侧邮件详情 - 完全复用主邮件详情区域的结构 -->
          <div v-if="historySelectedEmail" class="flex-1 bg-white flex flex-col min-h-0 overflow-hidden">
            <!-- 邮件头部 -->
            <div class="flex-shrink-0 p-4">
              <h2 class="text-lg font-medium text-gray-900 mb-3">{{ historySelectedEmail.subject }}</h2>
              
              <!-- 操作按钮 -->
              <div class="flex items-center space-x-3 mb-4">
                <button 
                  @click="openReplyModal(historySelectedEmail)" 
                  class="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded" 
                  title="回复邮件"
                >
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </button>
                <button 
                  @click="toggleEmailImportant(historySelectedEmail)"
                  :class="historySelectedEmail.is_important 
                    ? 'p-1.5 text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded' 
                    : 'p-1.5 text-gray-400 bg-gray-50 hover:bg-orange-50 hover:text-orange-600 rounded'"
                  :title="historySelectedEmail.is_important ? '取消重要标记' : '标记为重要'">
                  <StarIcon class="h-4 w-4" :class="historySelectedEmail.is_important ? 'fill-current' : ''" />
                </button>
                <button 
                  @click="toggleEmailRead(historySelectedEmail)"
                  :class="historySelectedEmail.is_read 
                    ? 'p-1.5 text-gray-400 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded' 
                    : 'p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded'"
                  :title="historySelectedEmail.is_read ? '标记为未读' : '标记为已读'">
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button 
                  @click="handleTranslateEmail"
                  class="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded" 
                  title="翻译邮件内容">
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </button>
                <button 
                  @click="openArchiveModal"
                  class="p-1.5 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded" 
                  title="归档邮件">
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </button>
                <div class="flex items-center space-x-2">
                  <span :class="getFolderStatusStyle(historySelectedEmail.folder_type)" class="px-2 py-1 text-xs rounded">
                    {{ getFolderStatusText(historySelectedEmail.folder_type) }}
                  </span>
                  <span
                    v-if="hasOutboundTracking(historySelectedEmail)"
                    :class="getOutboundTrackingBadgeClass(historySelectedEmail)"
                    class="px-2 py-1 text-xs rounded"
                    :title="getOutboundTrackingTitle(historySelectedEmail)"
                  >
                    {{ getOutboundTrackingLabel(historySelectedEmail) }}
                  </span>
                </div>
              </div>
              
              <!-- 分割线和展开详情按钮 -->
              <div class="relative mb-4 -mx-4">
                <div class="border-t border-gray-200"></div>
                <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <button 
                    @click="showHistoryEmailDetails = !showHistoryEmailDetails"
                    class="px-2 bg-white text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {{ showHistoryEmailDetails ? '收起详情 ▲' : '展开详情 ▼' }}
                  </button>
                </div>
              </div>
              
              <!-- 邮件详细信息 -->
              <div v-show="showHistoryEmailDetails" class="space-y-2 mb-4 pb-4 border-b border-gray-200 -mx-4 px-4">
                <div class="flex items-center justify-between text-xs text-gray-600">
                  <div class="flex items-center">
                    <span class="font-medium mr-1">发件人:</span>
                    <span>{{ historySelectedEmail.sender_name }} &lt;{{ historySelectedEmail.sender_email }}&gt;</span>
                  </div>
                  <button 
                    @click="copyToClipboard(historySelectedEmail.sender_email)"
                    class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="复制发件人邮箱"
                  >
                    <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div class="flex items-center justify-between text-xs text-gray-600">
                  <div class="flex items-center">
                    <span class="font-medium mr-1">收件人:</span>
                    <span>{{ historySelectedEmail.recipient_list }}</span>
                  </div>
                  <button 
                    @click="copyToClipboard(historySelectedEmail.recipient_list)"
                    class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="复制收件人邮箱"
                  >
                    <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div class="flex items-center text-xs text-gray-600">
                  <span class="font-medium mr-1">时间:</span>
                  <span>{{ formatDateTime(historySelectedEmail.received_date) }}</span>
                </div>
                <!-- 出站邮件打开追踪 -->
                <OutboundTrackingPanel :email="historySelectedEmail" />
              </div>
              
              <!-- 附件区域 - 历史往来 -->
              <div v-if="historySelectedEmail.attachments && historySelectedEmail.attachments.filter(a => !a.is_inline).length > 0" class="mb-4">
                <h4 class="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <PaperClipIcon class="inline-block h-4 w-4 mr-1" />
                  附件 ({{ historySelectedEmail.attachments.filter(a => !a.is_inline).length }})
                </h4>
                
                <!-- 3列网格附件展示 -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div
                    v-for="(attachment, index) in (showAllHistoryAttachments ? historySelectedEmail.attachments.filter(a => !a.is_inline) : historySelectedEmail.attachments.filter(a => !a.is_inline).slice(0, 3))"
                    :key="attachment.id"
                    class="flex items-center p-2 bg-gray-50 rounded border border-gray-200 hover:shadow-sm transition-all"
                  >
                    <!-- 文件图标 -->
                    <div class="flex-shrink-0 mr-3">
                      <svg v-if="getFileTypeFromContentType(attachment.content_type) === 'pdf'" class="h-10 w-10 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                      </svg>
                      <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'image'" class="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'video'" class="h-10 w-10 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'excel'" class="h-10 w-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                      </svg>
                      <svg v-else-if="getFileTypeFromContentType(attachment.content_type) === 'word'" class="h-10 w-10 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                      </svg>
                      <DocumentTextIcon v-else class="h-10 w-10 text-gray-500" />
                    </div>
                    
                    <!-- 文件信息 -->
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-medium text-gray-900 truncate" :title="attachment.filename || attachment.name">
                        {{ attachment.filename || attachment.name }}
                      </div>
                      <div class="flex items-center gap-2 mt-0.5">
                        <span class="text-xs text-gray-500">
                          {{ formatFileSize(attachment.size) }}
                        </span>
                        <button
                          v-if="canPreview(attachment.content_type)"
                          @click="previewFile(attachment)"
                          class="text-xs text-gray-600 hover:text-gray-900 transition-colors"
                        >
                          预览
                        </button>
                        <button
                          @click="downloadAttachment(attachment)"
                          :disabled="downloadingAttachments.has(attachment.id)"
                          class="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 transition-colors"
                        >
                          {{ downloadingAttachments.has(attachment.id) ? '下载中...' : '保存' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 展开更多按钮 -->
                <div v-if="historySelectedEmail.attachments.filter(a => !a.is_inline).length > 3" class="text-center mt-2">
                  <button 
                    @click="showAllHistoryAttachments = !showAllHistoryAttachments"
                    class="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {{ showAllHistoryAttachments ? '收起 ▲' : `展开更多 (${historySelectedEmail.attachments.filter(a => !a.is_inline).length - 3}) ▼` }}
                  </button>
                </div>
                
                <!-- 底部分割线 -->
                <div class="border-b border-gray-200 mt-4 -mx-4"></div>
              </div>
            </div>

            <!-- 邮件内容 -->
            <div class="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar px-4 pb-4 min-h-0" style="word-break: break-word;">
              <IsolatedEmailContent 
                :html-content="historySelectedEmail.body_html"
                :text-content="historySelectedEmail.body_text"
              />
            </div>
          </div>
          <!-- 加载中骨架屏 -->
          <div v-else-if="loadingHistoryDetail" class="flex-1 bg-white p-4 overflow-hidden">
            <div class="animate-pulse space-y-4">
              <div class="h-6 bg-gray-200 rounded w-3/4"></div>
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-1/2"></div>
                <div class="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
              <div class="border-t border-gray-200 pt-4 mt-4">
                <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div class="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
          <!-- 未选择邮件提示 -->
          <div v-else class="flex-1 bg-white flex items-center justify-center">
            <div class="text-center text-gray-400">
              <svg class="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p class="mt-2 text-sm">请选择左侧邮件查看详情</p>
            </div>
          </div>
          
        </div>
        
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import emailServiceV2 from '@/services/emailServiceV2'
import { customerService, customerTagService, customerGroupService } from '@/services/customerService'
import { productService } from '@/services/productService'
import FilePreview from '@/components/FilePreview.vue'
import IsolatedEmailContent from '@/components/IsolatedEmailContent.vue'
import EmailReplyModal from '@/components/EmailReplyModal.vue'
import OutboundTrackingPanel from '@/components/email/OutboundTrackingPanel.vue'
import { getApiBaseUrlSync, preloadConfig } from '@/services/configService'
import apiRequest from '@/utils/api'
import { isPublicObjectStorageUrl, toApiRelativePath } from '@/utils/attachmentUrl'
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  TrashIcon,
  InboxIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  StarIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  PaperClipIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  UserPlusIcon,
  FolderIcon,
  PlusIcon,
  AtSymbolIcon,
  EyeIcon,
  LanguageIcon,
  XMarkIcon,
  CogIcon,
  PencilSquareIcon
} from '@heroicons/vue/24/outline'

// 搜索状态
const searchQuery = ref('')
const accountSearchQuery = ref('') // 邮件账户搜索
const syncing = ref(false)
const syncingAccountId = ref(null) // 正在同步的账户ID（用于账户列表项同步按钮的加载动画）

// 文件夹列表折叠状态 - 从 localStorage 读取或使用默认值
const folderListCollapsed = ref(localStorage.getItem('email-folder-list-collapsed') === 'true')

// 邮件列表每页数量 - 从 localStorage 读取或使用默认值
const EMAIL_LIST_PAGE_SIZE_KEY = 'email-list-page-size'
const pageSizeOptions = [20, 50, 100]
const getSavedEmailPageSize = () => {
  const saved = parseInt(localStorage.getItem(EMAIL_LIST_PAGE_SIZE_KEY), 10)
  return pageSizeOptions.includes(saved) ? saved : 20
}

// 邮件账户排序 - 从 localStorage 读取或使用默认值
const EMAIL_ACCOUNT_SORT_KEY = 'email-list-account-sort-mode'
const EMAIL_ACCOUNT_ORDER_KEY = 'email-list-account-order'
const accountSortOptions = [
  { value: 'custom', label: '自定义' },
  { value: 'name_asc', label: '名称 A-Z' },
  { value: 'name_desc', label: '名称 Z-A' },
  { value: 'unread_desc', label: '未读优先' },
  { value: 'email_asc', label: '邮箱 A-Z' }
]
const validAccountSortModes = accountSortOptions.map(option => option.value)

const getSavedAccountSortMode = () => {
  const saved = localStorage.getItem(EMAIL_ACCOUNT_SORT_KEY)
  return validAccountSortModes.includes(saved) ? saved : 'custom'
}

const getSavedAccountOrder = () => {
  try {
    const raw = localStorage.getItem(EMAIL_ACCOUNT_ORDER_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const accountSortMode = ref(getSavedAccountSortMode())
const customAccountOrder = ref(getSavedAccountOrder())

const saveCustomAccountOrder = (order) => {
  customAccountOrder.value = order
  localStorage.setItem(EMAIL_ACCOUNT_ORDER_KEY, JSON.stringify(order))
}

const compareAccountDisplayName = (a, b) =>
  (a.name || a.email || '').localeCompare(b.name || b.email || '', 'zh-CN', { sensitivity: 'base' })

const mergeCustomAccountOrder = (accounts) => {
  const ids = accounts.map(account => account.id)
  const existing = customAccountOrder.value.filter(id => ids.includes(id))
  const newIds = ids.filter(id => !existing.includes(id))
  const merged = [...existing, ...newIds]
  if (JSON.stringify(merged) !== JSON.stringify(customAccountOrder.value)) {
    saveCustomAccountOrder(merged)
  }
}

const sortEmailAccountsList = (accounts) => {
  const list = [...accounts]

  switch (accountSortMode.value) {
    case 'name_asc':
      return list.sort(compareAccountDisplayName)
    case 'name_desc':
      return list.sort((a, b) => compareAccountDisplayName(b, a))
    case 'unread_desc':
      return list.sort((a, b) => {
        const unreadDiff = (b.unread_count || 0) - (a.unread_count || 0)
        return unreadDiff !== 0 ? unreadDiff : compareAccountDisplayName(a, b)
      })
    case 'email_asc':
      return list.sort((a, b) =>
        (a.email || '').localeCompare(b.email || '', 'zh-CN', { sensitivity: 'base' })
      )
    case 'custom':
    default: {
      const order = customAccountOrder.value
      return list.sort((a, b) => {
        let indexA = order.indexOf(a.id)
        let indexB = order.indexOf(b.id)
        if (indexA < 0) indexA = order.length
        if (indexB < 0) indexB = order.length
        if (indexA !== indexB) return indexA - indexB
        return compareAccountDisplayName(a, b)
      })
    }
  }
}

const handleAccountSortModeChange = () => {
  localStorage.setItem(EMAIL_ACCOUNT_SORT_KEY, accountSortMode.value)
  if (accountSortMode.value === 'custom') {
    mergeCustomAccountOrder(emailAccounts.value)
  }
}

const moveAccountInOrder = (account, direction) => {
  if (accountSortMode.value !== 'custom' || accountSearchQuery.value.trim()) return

  mergeCustomAccountOrder(emailAccounts.value)
  const order = [...customAccountOrder.value]
  const currentIndex = order.indexOf(account.id)
  if (currentIndex < 0) return

  const targetIndex = currentIndex + direction
  if (targetIndex < 0 || targetIndex >= order.length) return

  order.splice(currentIndex, 1)
  order.splice(targetIndex, 0, account.id)
  saveCustomAccountOrder(order)
}

// 同步状态
const autoSyncInterval = ref(null) // 历史遗留定时器句柄，仅用于 onUnmounted 兵底清理
const lastSyncTime = ref(null)
const syncProgress = ref(0)
const syncStatus = ref('idle') // idle, syncing, success, error
const lastSyncResult = ref(null) // 最后一次同步结果信息

// 回复邮件相关状态
const showReplyModal = ref(false)
const replyTargetEmail = ref(null)

// 移动邮件到收件箱相关状态
const movingToInbox = ref(false)

// 重新获取邮件内容状态
const refetchingContent = ref(false)

// 翻译相关状态
const translatingEmail = ref(false)
const showTranslation = ref(false)
const translatedHtml = ref('')
const translationCache = ref({}) // { emailId: translatedHtml }

// 新建邮件相关状态
const showComposeModal = ref(false)
const defaultFromAccountId = ref(null)  // 默认的发件账户ID

// 备注相关状态
const showNotesModal = ref(false)
const currentNotesAccount = ref(null)
const accountNotes = ref('')
const savingNotes = ref(false)

// 批量已读相关状态
const showBatchReadConfirm = ref(false)
const batchReadAccount = ref(null)
const batchReadLoading = ref(false)

// 历史往来相关状态
const showHistoryModal = ref(false)
const historyActiveTab = ref('all') // 'all' | 'received' | 'sent'
const historyEmailList = ref([])
const historySelectedEmail = ref(null)
const historyPagination = ref({
  currentPage: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0
})
const historyTabCounts = ref({
  all: 0,
  received: 0,
  sent: 0
})
const loadingHistory = ref(false)
const loadingHistoryDetail = ref(false)

// 历史往来对方邮箱（根据文件夹类型动态计算）
const historyContactEmail = computed(() => {
  if (!selectedEmail.value) return ''
  
  const folderType = selectedEmail.value.folder_type
  
  // 如果是已发送邮件，对方是收件人
  if (folderType === 'sent') {
    // 优先使用recipient_emails数组的第一个
    if (selectedEmail.value.recipient_emails && selectedEmail.value.recipient_emails.length > 0) {
      return selectedEmail.value.recipient_emails[0]
    }
    // 降级方案：从 recipient_list 字符串中提取第一个邮箱
    if (selectedEmail.value.recipient_list) {
      const match = selectedEmail.value.recipient_list.match(/([\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,})/)
      if (match) return match[1]
    }
    return selectedEmail.value.recipient_list || ''
  }
  
  // 其他情况（收件箱、垃圾邮件等），对方是发件人
  return selectedEmail.value.sender_email || ''
})

// 邮件详情展开/收起状态
const showEmailDetails = ref(false)
const showHistoryEmailDetails = ref(false)

// 附件展开/收起状态
const showAllAttachments = ref(false)
const showAllHistoryAttachments = ref(false)

// 加载状态
const loadingEmails = ref(false)
const loadingEmailContent = ref(false)
const loadingEmailId = ref(null)
const loadingAccounts = ref(false)
const loadingFolders = ref(false)
const loadingAccountSelection = ref(false)
const loadingAccountId = ref(null)
const loadingFolderSelection = ref(false)
const loadingFolderId = ref(null)
const clearingSelection = ref(false)

// 通知系统
const toast = useToast()

// 数据状态
const folders = ref([])
const emailAccounts = ref([])

watch(
  emailAccounts,
  (accounts) => {
    if (accountSortMode.value === 'custom' && accounts.length) {
      mergeCustomAccountOrder(accounts)
    }
  },
  { deep: true }
)

const emailList = ref([])
const selectedEmail = ref(null)
const selectedFolder = ref(null)
const selectedAccount = ref('all')
const selectedAccountFolder = ref(null)

// 分页状态
const currentPage = ref(1)
const totalEmails = ref(0)
const pageSize = ref(getSavedEmailPageSize())
const jumpToPage = ref(null)

// 文件预览状态
const showFilePreview = ref(false)
const previewedFile = ref(null)

// 附件下载状态
const downloadingAttachments = ref(new Set())

// 归档状态
const showArchiveModal = ref(false)
const selectedArchiveFolder = ref(null)
const availableArchiveFolders = ref([])
const existingArchives = ref([])  // 现有的归档状态
const loadingArchives = ref(false)  // 加载归档状态
const deletingFolderId = ref(null)  // 正在删除的文件夹ID
const archivingToFolder = ref(false)  // 归档到文件夹中
const unarchivingFromFolder = ref(null)  // 取消归档中的文件夹ID

// 初始化API数据加载方法 - 使用新的初始化 API
const initializeData = async () => {
  try {
    loadingAccounts.value = true
    loadingFolders.value = true
    loadingTags.value = true
    loadingGroups.value = true
    
    // 设置默认选中项（这样邮件列表可以立即开始加载）
    selectedAccount.value = 'all'
    
    // 🚀 使用新的初始化 API - 一次性获取所有数据
    const [initResult, emailsResult] = await Promise.all([
      emailServiceV2.initializeEmailManagement(),
      loadEmailsInitial() // 并行加载邮件列表
    ])
    
    if (initResult.success && initResult.data) {
      const data = initResult.data
      
      // 1. 设置账户数据
      emailAccounts.value = (data.accounts || []).map(account => ({
        ...account,
        name: account.display_name || account.email,
        status: account.is_active ? 'active' : 'inactive',
        expanded: false,
        folders: []
      }))
      mergeCustomAccountOrder(emailAccounts.value)
      
      // 2. 设置聚合文件夹统计
      const stats = data.aggregated_stats || {}
      folders.value = [
        { id: 'all', name: '全部邮件', folder_type: 'all', icon: EnvelopeIcon, count: stats.all || 0 },
        { id: 'inbox', name: '收件箱', folder_type: 'inbox', icon: InboxIcon, count: stats.inbox || 0 },
        { id: 'sent', name: '已发送', folder_type: 'sent', icon: PaperAirplaneIcon, count: stats.sent || 0 },
        { id: 'unread', name: '未读邮件', folder_type: 'unread', icon: DocumentTextIcon, count: stats.unread || 0 },
        { id: 'important', name: '重要邮件', folder_type: 'important', icon: StarIcon, count: stats.important || 0 },
        { id: 'spam', name: '垃圾邮件', folder_type: 'spam', icon: ExclamationTriangleIcon, count: stats.spam || 0 }
      ]
      
      // 3. 设置账户文件夹
      const bulkFolders = data.folders || {}
      for (const account of emailAccounts.value) {
        const accountFolders = bulkFolders[account.id.toString()] || []
        account.folders = accountFolders.map(folder => ({
          ...folder,
          name: getFolderChineseName(folder),
          displayName: getFolderChineseName(folder),
          count: folder.total_emails || 0,
          icon: getFolderIcon(folder.folder_type)
        }))
      }
      
      // 4. 设置标签和分组
      availableTags.value = data.tags || []
      availableGroups.value = data.groups || []
      
      // 标记标签和分组已加载
      if (data.tags && data.tags.length >= 0) tagsLoaded.value = true
      if (data.groups && data.groups.length >= 0) groupsLoaded.value = true
      
      // 5. 设置默认选中的聚合文件夹
      selectedFolder.value = folders.value[0] // 默认选中"全部邮件"
      
    } else {
      // 降级：使用原有的多个请求
      console.warn('初始化API失败，使用降级方案:', initResult.error || '数据不完整')
      
      const [accountsResult, folderStatsResult] = await Promise.all([
        emailServiceV2.getEmailAccounts({ page_size: 10000 }),
        emailServiceV2.getAggregatedFolderStats()
      ])
    
    if (accountsResult.success) {
      emailAccounts.value = accountsResult.results.map(account => ({
        ...account,
        name: account.display_name || account.email,
        status: account.is_active ? 'active' : 'inactive',
        expanded: false,
        folders: []
      }))
      mergeCustomAccountOrder(emailAccounts.value)
      
      folders.value = [
        { id: 'all', name: '全部邮件', folder_type: 'all', icon: EnvelopeIcon, count: folderStatsResult.all || 0 },
        { id: 'inbox', name: '收件箱', folder_type: 'inbox', icon: InboxIcon, count: folderStatsResult.inbox || 0 },
        { id: 'sent', name: '已发送', folder_type: 'sent', icon: PaperAirplaneIcon, count: folderStatsResult.sent || 0 },
        { id: 'unread', name: '未读邮件', folder_type: 'unread', icon: DocumentTextIcon, count: folderStatsResult.unread || 0 },
        { id: 'important', name: '重要邮件', folder_type: 'important', icon: StarIcon, count: folderStatsResult.important || 0 },
        { id: 'spam', name: '垃圾邮件', folder_type: 'spam', icon: ExclamationTriangleIcon, count: folderStatsResult.spam || 0 }
      ]
      
        selectedFolder.value = folders.value[0]
      
        // 后台加载文件夹
        loadAccountFolders()

        // 后台加载标签和分组
        loadCustomerTags()
        loadCustomerGroups()
      }
    }
  } catch (error) {
    console.error('初始化邮件数据失败:', error)
    toast.error(error.error || '加载邮件数据失败')
  } finally {
    loadingAccounts.value = false
    loadingFolders.value = false
    loadingTags.value = false
    loadingGroups.value = false
  }
}

// API集成方法
const getFolderIcon = (folderType) => {
  const iconMap = {
    'inbox': InboxIcon,
    'sent': PaperAirplaneIcon,
    'drafts': DocumentTextIcon,
    'trash': TrashIcon,
    'spam': ExclamationTriangleIcon,
    'unread': DocumentTextIcon,
    'important': StarIcon,
    'archive': FolderIcon,
    'custom': FolderIcon,
    'user_created': FolderIcon
  }
  return iconMap[folderType] || FolderIcon
}

// 后端直接返回标准系统文件夹，前端只需要转换名称和图标

const getFolderChineseName = (folder) => {
  const { folder_type, name } = folder
  
  // 系统文件夹的中文名称映射
  const systemFolderNames = {
    'inbox': '收件箱',
    'sent': '已发送', 
    'spam': '垃圾邮件',
    'unread': '未读邮件',
    'important': '重要邮件',
    'custom': name, // 邮件同步的自定义文件夹保持原名称
    'user_created': name // 用户手动创建的文件夹保持原名称
  }
  
  // 如果是系统文件夹，返回中文名称
  if (systemFolderNames[folder_type]) {
    return systemFolderNames[folder_type]
  }
  
  // 对于一些常见的英文文件夹名称，也进行转换
  const nameMapping = {
    'INBOX': '收件箱',
    'Inbox': '收件箱',
    'inbox': '收件箱',
    'SENT': '已发送',
    'Sent': '已发送',
    'sent': '已发送',
    'Sent Items': '已发送',
    'Sent Messages': '已发送',
    '[Gmail]/Sent Mail': '已发送',
    'SPAM': '垃圾邮件',
    'Spam': '垃圾邮件',
    'spam': '垃圾邮件',
    'Junk': '垃圾邮件',
    'Junk Email': '垃圾邮件',
    '[Gmail]/Spam': '垃圾邮件',
    '[Gmail]/Trash': '已删除',
    '[Gmail]/Drafts': '草稿',
    '[Gmail]/All Mail': '所有邮件',
    '[Gmail]/Starred': '星标邮件',
    '[Gmail]/Important': '重要邮件'
  }
  
  // 检查是否有对应的中文名称
  if (nameMapping[name]) {
    return nameMapping[name]
  }
  
  // 如果已经是中文，直接返回
  if (/[\u4e00-\u9fa5]/.test(name)) {
    return name
  }
  
  // 过滤掉编码字符，如果是系统文件夹但名称不规范，尝试识别
  const lowerName = name.toLowerCase()
  if (lowerName.includes('inbox') || name === 'INBOX') {
    return '收件箱'
  }
  if (lowerName.includes('sent') || name.includes('发送') || name.includes('发件')) {
    return '已发送'
  }
  if (lowerName.includes('spam') || lowerName.includes('junk') || name.includes('垃圾')) {
    return '垃圾邮件'
  }
  
  // 默认返回原名称（对于用户自建文件夹）
  return name
}

// 直接转换文件夹名称字符串为中文（用于邮件列表显示）
const translateFolderName = (folderName) => {
  if (!folderName) return ''
  
  // 常见文件夹名称映射
  const nameMapping = {
    'INBOX': '收件箱',
    'Inbox': '收件箱', 
    'inbox': '收件箱',
    'SENT': '已发送',
    'Sent': '已发送',
    'sent': '已发送',
    'Sent Items': '已发送',
    'Sent Messages': '已发送',
    '[Gmail]/Sent Mail': '已发送',
    '[Gmail]/已发邮件': '已发邮件',
    '[Gmail]/&XfJT0ZCuTvY-': '已发邮件',
    '&XfJT0ZAB-': '已发送',
    'SPAM': '垃圾邮件',
    'Spam': '垃圾邮件',
    'spam': '垃圾邮件',
    'Junk': '垃圾邮件',
    'Junk Email': '垃圾邮件',
    '[Gmail]/Spam': '垃圾邮件',
    '[Gmail]/&V4NXPpCuTvY-': '垃圾邮件',
    '&V4NXPpCuTvY-': '垃圾邮件',
    'Trash': '垃圾箱',
    'Deleted Items': '已删除邮件',
    '[Gmail]/Trash': '已删除',
    '[Gmail]/&XfJSIJZkkK5O9g-': '已删除',
    'Drafts': '草稿箱',
    '[Gmail]/Drafts': '草稿',
    '[Gmail]/&g0l6Pw-': '草稿',
    'Archive': '归档',
    '[Gmail]/All Mail': '所有邮件',
    '[Gmail]/&YkBnCZCuTvY-': '所有邮件',
    '[Gmail]/Starred': '星标邮件',
    '[Gmail]/&XfJSoGYfaAc-': '星标邮件',
    '[Gmail]/Important': '重要邮件',
    '[Gmail]/&kc2JgQ-': '重要邮件'
  }
  
  // 如果有对应的中文名称，返回中文
  if (nameMapping[folderName]) {
    return nameMapping[folderName]
  }
  
  // 如果已经是中文，直接返回
  if (/[\u4e00-\u9fa5]/.test(folderName)) {
    return folderName
  }
  
  // 默认返回原名称
  return folderName
}

// 获取文件夹状态样式
const getFolderStatusStyle = (folderType) => {
  const styleMapping = {
    'inbox': 'bg-blue-100 text-blue-700',
    'sent': 'bg-green-100 text-green-700',
    'spam': 'bg-red-100 text-red-700',
    'trash': 'bg-gray-100 text-gray-700',
    'draft': 'bg-yellow-100 text-yellow-700',
    'archive': 'bg-purple-100 text-purple-700',
    'custom': 'bg-indigo-100 text-indigo-700',
    'user_created': 'bg-teal-100 text-teal-700'
  }
  
  return styleMapping[folderType] || 'bg-gray-100 text-gray-600'
}

// 获取文件夹状态文本
const getFolderStatusText = (folderType) => {
  const textMapping = {
    'inbox': '收件箱',
    'sent': '已发送',
    'spam': '垃圾邮件',
    'trash': '已删除',
    'draft': '草稿',
    'archive': '已归档',
    'custom': '自定义',
    'user_created': '用户创建'
  }
  
  return textMapping[folderType] || '其他'
}

// 出站邮件打开追踪（系统发送的回复/撰写邮件）
const hasOutboundTracking = (email) => {
  if (!email) return false
  // 仅对已发送邮件显示打开追踪，收件箱收到的邮件不显示
  if (email.folder_type !== 'sent') return false
  return (email.outbound_reply_count || 0) > 0
}

const getOutboundTrackingBadgeClass = (email) => {
  if (email?.outbound_is_opened) {
    return 'bg-purple-100 text-purple-800'
  }
  return 'bg-gray-100 text-gray-600'
}

const getOutboundTrackingLabel = (email) => {
  if (!email) return ''
  if (email.outbound_is_opened) {
    const count = email.outbound_open_count || 0
    return count > 1 ? `已打开 ${count}次` : '已打开'
  }
  if ((email.outbound_reply_count || 0) > 0) {
    return '未打开'
  }
  return ''
}

const getOutboundTrackingTitle = (email) => {
  if (!email || !hasOutboundTracking(email)) return ''
  const parts = [`发送 ${email.outbound_reply_count || 0} 次`]
  if (email.outbound_is_opened) {
    parts.push(`打开 ${email.outbound_open_count || 0} 次`)
    if (email.outbound_first_opened_at) {
      parts.push(`首次打开 ${formatDateTime(email.outbound_first_opened_at)}`)
    }
  } else {
    parts.push('对方尚未打开')
  }
  return parts.join(' · ')
}

const loadAccountFolders = async () => {
  try {
    // 🚀 批量获取所有账户的文件夹 - 只需一次请求
    const bulkFolders = await emailServiceV2.getBulkAccountFolders()
    
    // bulkFolders 格式: { "accountId": [folders], "accountId2": [folders], ... }
    for (const account of emailAccounts.value) {
      const accountFolders = bulkFolders[account.id.toString()] || []
      
      // 转换为中文名称并设置图标
      account.folders = accountFolders.map(folder => ({
        ...folder,
        name: getFolderChineseName(folder),
        displayName: getFolderChineseName(folder),
        count: folder.total_emails || 0,
        icon: getFolderIcon(folder.folder_type)
      }))
    }
  } catch (error) {
    console.error('批量加载账户文件夹失败:', error)
    // 如果批量加载失败，降级到逐个加载
    console.warn('降级到逐个加载文件夹...')
    try {
      for (const account of emailAccounts.value) {
        const standardFolders = await emailServiceV2.getAccountFolders(account.id)
        account.folders = standardFolders.map(folder => ({
          ...folder,
          name: getFolderChineseName(folder),
          displayName: getFolderChineseName(folder),
          count: folder.total_emails || 0,
          icon: getFolderIcon(folder.folder_type)
        }))
      }
    } catch (fallbackError) {
      console.error('降级加载也失败:', fallbackError)
    }
  }
}

const loadEmails = async (params = {}) => {
  try {
    loadingEmails.value = true
    
    const searchParams = {
      page: currentPage.value,
      page_size: pageSize.value,
      ordering: '-received_date',
      ...params
    }
    
    // 如果没有传递自定义参数，根据当前选中状态构建查询参数
    if (Object.keys(params).length === 0) {
      // 优先处理账户文件夹（selectedAccountFolder）
      if (selectedAccountFolder.value) {
        const folder = selectedAccountFolder.value
        const account = selectedAccount.value
        
        // 设置账户参数
        if (account && account !== 'all') {
          searchParams.account = account.id || account
        }
        
        // 根据文件夹类型设置过滤参数
        if (folder.folder_type === 'unread') {
          searchParams.is_read = false
        } else if (folder.folder_type === 'important') {
          searchParams.is_important = true
        } else if (folder.folder_type === 'user_created') {
          searchParams.archived_folder = folder.id
        } else {
          searchParams.folder__folder_type = folder.folder_type
        }
      }
      // 否则处理聚合文件夹（selectedFolder）
      else if (selectedFolder.value && selectedFolder.value.folder_type) {
        const folderType = selectedFolder.value.folder_type
        
        switch (folderType) {
          case 'all':
            // 全部邮件 - 不添加额外过滤条件
            break
          case 'inbox':
            searchParams['folder__folder_type'] = 'inbox'
            break
          case 'sent':
            searchParams['folder__folder_type'] = 'sent'
            break
          case 'unread':
            searchParams['is_read'] = false
            break
          case 'important':
            searchParams['is_important'] = true
            break
          case 'spam':
            searchParams['folder__folder_type'] = 'spam'
            break
          default:
            // 对于账户文件夹，使用folder_type
            searchParams['folder__folder_type'] = folderType
        }
        
        // 如果选中了特定账户
        if (selectedAccount.value && selectedAccount.value !== 'all') {
          searchParams.account = selectedAccount.value.id || selectedAccount.value
        }
      }
    }
    
    
    const result = await emailServiceV2.getEmails(searchParams)
    
    
    if (result.success) {
      emailList.value = result.results
      totalEmails.value = result.count
    }
  } catch (error) {
    console.error('加载邮件列表失败:', error)
    toast.error(error.error || '加载邮件失败')
  } finally {
    loadingEmails.value = false
  }
}

// 初始邮件加载方法（用于页面首次加载，默认加载全部邮件）
const loadEmailsInitial = async () => {
  try {
    loadingEmails.value = true
    
    const searchParams = {
      page: 1, // 第一页
      page_size: pageSize.value,
      ordering: '-received_date'
      // 不添加任何过滤条件，默认加载全部邮件
    }
    
    currentPage.value = 1
    
    const result = await emailServiceV2.getEmails(searchParams)
    
    if (result.success) {
      emailList.value = result.results
      totalEmails.value = result.count
    }
  } catch (error) {
    console.error('加载初始邮件列表失败:', error)
    // 初始加载失败时不显示错误提示，避免干扰用户
  } finally {
    loadingEmails.value = false
  }
}

const loadEmailContent = async (emailId) => {
  try {
    loadingEmailContent.value = true
    loadingEmailId.value = emailId
    
    // 获取邮件详情（V2 API支持按需内容加载）
    const email = await emailServiceV2.getEmail(emailId)
    
    // 🔧 更新选中邮件的完整内容（保持已有的基本信息，添加详细内容）
    selectedEmail.value = {
      ...selectedEmail.value,
      ...email
    }
    
    // 检查邮件内容是否为空，如果为空则尝试按需获取
    const needsContentFetch = !email.body_html && !email.body_text
    if (needsContentFetch) {
      toast.info('正在获取邮件完整内容...')
      
      try {
        const fetchResult = await emailServiceV2.fetchEmailContent(emailId)
        if (fetchResult.success && fetchResult.content_exists) {
          // 更新邮件内容
          selectedEmail.value = {
            ...selectedEmail.value,
            body_html: fetchResult.body_html,
            body_text: fetchResult.body_text
          }
          toast.success('邮件内容加载完成')
        } else {
          console.log(`⚠️ 按需获取邮件内容失败: ${emailId}`)
          toast.warning('无法获取邮件完整内容，请稍后重试')
        }
      } catch (contentError) {
        console.error('按需获取邮件内容失败:', contentError)
        toast.error('获取邮件内容失败，请稍后重试')
      }
    }
    
    // ✅ 正文已就绪：立即隐藏骨架屏显示邮件内容。
    //    标记已读 / 刷新文件夹统计等操作与"正文显示"无关，放到后台异步进行，
    //    避免它们的串行网络请求（mark_read、aggregated_stats、folders）拖住骨架屏，
    //    也让内联图片在正文显示后再渐进加载，而不是卡着骨架等全部请求完成。
    loadingEmailContent.value = false
    loadingEmailId.value = null

    // 检查邮件列表中的未读状态
    const emailInList = emailList.value.find(e => e.id === emailId)
    const isUnreadInList = emailInList && !emailInList.is_read
    
    
    // 如果邮件在列表中显示为未读，标记为已读（后台进行，不阻塞正文展示）
    if (isUnreadInList) {
      try {
        await emailServiceV2.markEmailRead(emailId, true)
        
        // 更新邮件列表中的状态
        const emailIndex = emailList.value.findIndex(e => e.id === emailId)
        if (emailIndex !== -1) {
          emailList.value[emailIndex].is_read = true
        }
        
        // 更新选中邮件的状态
        if (selectedEmail.value && selectedEmail.value.id === emailId) {
          selectedEmail.value.is_read = true
        }
        
        // 更新文件夹统计
        const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
        await refreshFolderStats(accountId)
        
        // 同时更新邮件所属账户的文件夹统计
        if (email.account) {
          await refreshAccountFolderStats(email.account)
        }
    
      } catch (readError) {
        console.error('自动标记邮件已读失败:', readError)
        toast.error('标记邮件已读失败')
      }
    }
  } catch (error) {
    console.error('加载邮件详情失败:', error)
    toast.error(error.error || '加载邮件详情失败')
  } finally {
    loadingEmailContent.value = false
    loadingEmailId.value = null
  }
}

// 页面事件处理方法
const selectEmail = async (email) => {
  if (loadingEmailId.value === email.id) return
  
  // 切换邮件时重置翻译状态
  showTranslation.value = false
  translatedHtml.value = ''
  
  // 🔧 立即设置选中的邮件并开始客户信息加载
  selectedEmail.value = email
  
  // 并行执行邮件内容加载
  await loadEmailContent(email.id)
}

const toggleEmailImportant = async (email) => {
  try {
    const newImportantStatus = !email.is_important
    
    console.log(`⭐ [DEBUG] 切换邮件重要状态:`, {
      emailId: email.id,
      currentStatus: email.is_important,
      newStatus: newImportantStatus
    })
    
    await emailServiceV2.markEmailImportant(email.id, newImportantStatus)
    
    // 更新选中邮件的状态（右侧详情页）
    email.is_important = newImportantStatus
    
    // 更新邮件列表中的状态（左侧列表）
    const emailInList = emailList.value.find(e => e.id === email.id)
    if (emailInList) {
      emailInList.is_important = newImportantStatus
    }
    
    // 如果选中的邮件状态被更新，同步选中邮件对象
    if (selectedEmail.value && selectedEmail.value.id === email.id) {
      selectedEmail.value.is_important = newImportantStatus
    }
    
    toast.success(`邮件${newImportantStatus ? '已标记为重要' : '已取消重要标记'}`)
    
    // 更新文件夹统计
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)
    
    // 同时更新邮件所属账户的文件夹统计
    if (email.account) {
      await refreshAccountFolderStats(email.account)
    }
    
    
  } catch (error) {
    console.error('标记重要状态失败:', error)
    toast.error(error.error || '操作失败')
  }
}

const toggleEmailRead = async (email) => {
  try {
    const newReadStatus = !email.is_read
    
    
    await emailServiceV2.markEmailRead(email.id, newReadStatus)
    
    // 更新选中邮件的状态（右侧详情页）
    email.is_read = newReadStatus
    
    // 更新邮件列表中的状态（左侧列表）
    const emailInList = emailList.value.find(e => e.id === email.id)
    if (emailInList) {
      emailInList.is_read = newReadStatus
    }
    
    // 如果选中的邮件状态被更新，同步选中邮件对象
    if (selectedEmail.value && selectedEmail.value.id === email.id) {
      selectedEmail.value.is_read = newReadStatus
    }
    
    // 同步更新历史往来列表中的邮件状态（如果存在）
    const emailInHistoryList = historyEmailList.value.find(e => e.id === email.id)
    if (emailInHistoryList) {
      emailInHistoryList.is_read = newReadStatus
    }
    
    // 同步更新历史往来选中的邮件状态
    if (historySelectedEmail.value && historySelectedEmail.value.id === email.id) {
      historySelectedEmail.value.is_read = newReadStatus
    }
    
    toast.success(`邮件${newReadStatus ? '已标记为已读' : '已标记为未读'}`)
    
    // 更新文件夹统计
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)
    
    // 同时更新邮件所属账户的文件夹统计
    if (email.account) {
      await refreshAccountFolderStats(email.account)
    }
    
    
  } catch (error) {
    console.error('标记读取状态失败:', error)
    toast.error(error.error || '操作失败')
  }
}

// 批量标记已读 - 确认弹窗
const confirmBatchMarkRead = (account) => {
  batchReadAccount.value = account
  showBatchReadConfirm.value = true
}

// 批量标记已读 - 执行
const executeBatchMarkRead = async () => {
  if (!batchReadAccount.value || batchReadLoading.value) return
  
  try {
    batchReadLoading.value = true
    const account = batchReadAccount.value
    
    const result = await emailServiceV2.batchMarkAccountRead(account.id)
    
    if (result.success) {
      // 更新本地账户未读计数
      account.unread_count = 0
      
      // 更新邮件列表中该账户的邮件为已读
      emailList.value.forEach(email => {
        if (email.account === account.id) {
          email.is_read = true
        }
      })
      
      // 更新选中邮件状态
      if (selectedEmail.value && selectedEmail.value.account === account.id) {
        selectedEmail.value.is_read = true
      }
      
      // 刷新文件夹统计
      const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
      await refreshFolderStats(accountId)
      await refreshAccountFolderStats(account.id)
      
      toast.success(result.message || `已将 ${result.updated_count} 封邮件标记为已读`)
    } else {
      toast.error(result.error || '批量标记已读失败')
    }
  } catch (error) {
    console.error('批量标记已读失败:', error)
    toast.error(error.error || '批量标记已读失败')
  } finally {
    batchReadLoading.value = false
    showBatchReadConfirm.value = false
    batchReadAccount.value = null
  }
}

// 移动邮件到收件箱
const moveEmailToInbox = async (email) => {
  if (movingToInbox.value) return
  
  try {
    movingToInbox.value = true
    
    const result = await emailServiceV2.moveToInbox(email.id)
    
    if (result.success) {
      toast.success(result.message || '邮件已移动到收件箱')
      
      // 从当前邮件列表中移除该邮件
      const emailIndex = emailList.value.findIndex(e => e.id === email.id)
      if (emailIndex > -1) {
        emailList.value.splice(emailIndex, 1)
      }
      
      // 清空选中的邮件
      selectedEmail.value = null
      
      // 刷新文件夹统计
      const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
      await refreshFolderStats(accountId)
      
      // 刷新账户文件夹统计
      if (email.account) {
        await refreshAccountFolderStats(email.account)
      }
    } else {
      toast.error(result.error || '移动邮件失败')
    }
  } catch (error) {
    console.error('移动邮件到收件箱失败:', error)
    toast.error(error.error || '移动邮件失败')
  } finally {
    movingToInbox.value = false
  }
}

// 处理翻译邮件请求
const handleTranslateEmail = async () => {
  if (!selectedEmail.value) return
  
  // 如果已显示翻译，切回原文
  if (showTranslation.value) {
    showTranslation.value = false
    return
  }
  
  const emailId = selectedEmail.value.id
  
  // 检查缓存
  if (translationCache.value[emailId]) {
    translatedHtml.value = translationCache.value[emailId]
    showTranslation.value = true
    return
  }
  
  // 调用翻译 API
  translatingEmail.value = true
  try {
    const response = await apiRequest.post(`/api/emails/v2/emails/${emailId}/translate/`)
    if (response.data && response.data.success) {
      translatedHtml.value = response.data.translated_html
      translationCache.value[emailId] = response.data.translated_html
      showTranslation.value = true
      if (response.data.error) {
        toast.info(response.data.error)
      }
    } else {
      toast.error(response.data?.error || '翻译失败')
    }
  } catch (error) {
    console.error('翻译失败:', error)
    toast.error(error.response?.data?.error || '翻译服务暂时不可用，请稍后再试')
  } finally {
    translatingEmail.value = false
  }
}

// 强制重新获取邮件内容
const refetchEmailContent = async (email) => {
  if (!email || refetchingContent.value) return
  
  refetchingContent.value = true
  try {
    const response = await apiRequest.post(`/api/emails/v2/emails/${email.id}/refetch-content/`)
    if (response.data && response.data.success) {
      // 更新选中邮件的完整数据
      if (response.data.data) {
        // 给内联图片URL加时间戳破坏浏览器缓存
        const data = response.data.data
        if (data.body_html) {
          const ts = Date.now()
          data.body_html = data.body_html.replace(
            /(\/api\/emails\/v2\/attachments\/inline\/[^"']+)/g,
            (match) => {
              const sep = match.includes('?') ? '&' : '?'
              return `${match}${sep}_t=${ts}`
            }
          )
        }
        Object.assign(email, data)
        if (selectedEmail.value && selectedEmail.value.id === email.id) {
          selectedEmail.value = { ...email }
        }
      }
      toast.success('邮件内容已刷新')
    } else {
      toast.error(response.data?.message || '刷新失败')
    }
  } catch (error) {
    console.error('刷新邮件内容失败:', error)
    toast.error('刷新邮件内容失败，请稍后重试')
  } finally {
    refetchingContent.value = false
  }
}

// 显示归档模态框
const openArchiveModal = async () => {
  if (!selectedEmail.value) {
    toast.error('请先选择要归档的邮件')
    return
  }
  
  try {
    loadingArchives.value = true
    showArchiveModal.value = true
    
    // 获取当前邮件所属账户的文件夹
    const emailAccountId = selectedEmail.value.account
    const account = emailAccounts.value.find(acc => acc.id === emailAccountId)
    
    if (!account || !account.folders) {
      toast.error('无法获取账户文件夹信息')
      return
    }
    
    // 过滤出用户创建的文件夹（排除系统文件夹）
    availableArchiveFolders.value = account.folders.filter(folder => 
      folder.folder_type === 'user_created' || folder.folder_type === 'custom'
    )
    
    if (availableArchiveFolders.value.length === 0) {
      toast.warning('该账户暂无自定义文件夹，请先创建文件夹')
      closeArchiveModal()
      return
    }
    
    // 获取现有的归档状态
    const archiveResult = await emailServiceV2.getEmailArchives(selectedEmail.value.id)
    if (archiveResult.success) {
      existingArchives.value = archiveResult.archives || []
      
      // 为每个可选文件夹添加归档状态和统计数据
      availableArchiveFolders.value = availableArchiveFolders.value.map(folder => {
        const existingArchive = existingArchives.value.find(archive => archive.folder_id === folder.id)
        return {
          ...folder,
          isArchived: !!existingArchive,
          archiveData: existingArchive || null,
          // 使用归档数据中的统计，如果没有则使用文件夹原有统计
          displayName: folder.name,
          count: existingArchive ? existingArchive.total_emails : (folder.total_emails || 0),
          unreadCount: existingArchive ? existingArchive.unread_emails : (folder.unread_emails || 0)
        }
      })
    }
    
    selectedArchiveFolder.value = null
    
  } catch (error) {
    console.error('打开归档模态框失败:', error)
    toast.error('获取归档信息失败')
    closeArchiveModal()
  } finally {
    loadingArchives.value = false
  }
}

// 选择归档文件夹
const selectArchiveFolder = (folder) => {
  selectedArchiveFolder.value = folder
}

// 关闭归档模态框
const closeArchiveModal = () => {
  showArchiveModal.value = false
  selectedArchiveFolder.value = null
  availableArchiveFolders.value = []
  existingArchives.value = []
  loadingArchives.value = false
  archivingToFolder.value = false
  unarchivingFromFolder.value = null
}

// 执行归档操作
const executeArchive = async () => {
  if (!selectedEmail.value || !selectedArchiveFolder.value) {
    toast.error('请选择要归档的邮件和目标文件夹')
    return
  }
  
  try {
    archivingToFolder.value = true
    
    // 调用API归档邮件到指定文件夹（保持原文件夹）
    await emailServiceV2.moveEmailToFolder(selectedEmail.value.id, selectedArchiveFolder.value.id)
    
    // 注意：邮件保持在原文件夹，不从列表中移除
    // 只有当前查看的是该归档文件夹时，才会在刷新后显示该邮件
    
    // 更新统计数据
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)
    
    // 更新账户文件夹统计
    if (selectedEmail.value?.account) {
      await refreshAccountFolderStats(selectedEmail.value.account)
    }
    
    toast.success(`邮件已归档到 ${selectedArchiveFolder.value.displayName}`)
    closeArchiveModal()
    
    // 如果当前正在查看目标归档文件夹，需要刷新邮件列表以显示新归档的邮件
    if (selectedAccountFolder.value && 
        selectedAccountFolder.value.folder_type === 'user_created' &&
        selectedAccountFolder.value.id === selectedArchiveFolder.value.id) {
      // 构建正确的查询参数，包括归档文件夹参数和分页信息
      const searchParams = {
        account: selectedAccount.value.id,
        archived_folder: selectedAccountFolder.value.id,
        page: currentPage.value,
        page_size: pageSize.value
      }
      // 刷新邮件列表
      await loadEmails(searchParams)
    }
    
  } catch (error) {
    console.error('归档邮件失败:', error)
    toast.error(error.error || '归档失败，请重试')
  } finally {
    archivingToFolder.value = false
  }
}

// 取消归档操作
const executeUnarchive = async (folder) => {
  if (!selectedEmail.value || !folder) {
    return
  }

  try {
    unarchivingFromFolder.value = folder.id
    
    // 调用API取消归档
    await emailServiceV2.removeFromFolder(selectedEmail.value.id, folder.id)
    
    // 更新统计数据
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)
    
    // 更新账户文件夹统计
    if (selectedEmail.value?.account) {
      await refreshAccountFolderStats(selectedEmail.value.account)
    }
    
    toast.success(`已从 ${folder.displayName} 取消归档`)
    
    // 重新加载归档模态框数据
    await openArchiveModal()
    
    // 如果当前正在查看用户自定义文件夹（归档文件夹），且取消的归档就是当前查看的文件夹，需要刷新邮件列表
    if (selectedAccountFolder.value && 
        selectedAccountFolder.value.folder_type === 'user_created' &&
        selectedAccountFolder.value.id === folder.id) {
      // 清除选中的邮件
      selectedEmail.value = null
      // 构建正确的查询参数，包括归档文件夹参数和分页信息
      const searchParams = {
        account: selectedAccount.value.id,
        archived_folder: selectedAccountFolder.value.id,
        page: currentPage.value,
        page_size: pageSize.value
      }
      // 刷新邮件列表
      await loadEmails(searchParams)
    }
    
  } catch (error) {
    console.error('取消归档失败:', error)
    toast.error(error.error || '取消归档失败，请重试')
  } finally {
    unarchivingFromFolder.value = null
  }
}

const syncEmails = async (isAutoSync = false, targetAccount = null) => {
  // 同步目标：优先使用显式传入的账户（来自账户列表项的同步按钮），否则回退到当前选中账户
  const syncTarget = targetAccount !== null ? targetAccount : selectedAccount.value

  // 仅支持单账户同步（批量同步已由后端 Celery 定时任务接管）
  if (!syncTarget || syncTarget === 'all') {
    return
  }

  try {
    syncing.value = true
    syncStatus.value = 'syncing'
    syncProgress.value = 0

    let totalNewEmails = 0
    let totalEmails = 0

    const result = await emailServiceV2.syncAccount(syncTarget.id)

    // 检查同步是否成功
    if (!result.success) {
      throw {
        error: result.error || '邮件同步失败',
        detail: result.detail || result.error || '邮件同步失败'
      }
    }

    if (result.data && result.data.data) {
      // V2 API 返回双重嵌套结构：result.data.data
      const syncData = result.data.data
      totalNewEmails += syncData.new_emails || 0
      totalEmails += syncData.total_emails || 0
    }

    // 保存同步结果
    lastSyncResult.value = {
      new_emails: totalNewEmails,
      total_emails: totalEmails,
      synced_accounts: 1
    }

    syncProgress.value = 100
    syncStatus.value = 'success'
    lastSyncTime.value = new Date()
    syncing.value = false

    if (!isAutoSync) {
      toast.success(totalNewEmails > 0 ? `同步完成，新增 ${totalNewEmails} 封邮件` : '同步完成，暂无新邮件')
    }

    // 重新加载文件夹统计和邮件列表
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)

    // 🔥 检查是否有搜索关键词，有则保持搜索状态
    if (searchQuery.value.trim()) {
      await performSearch()
    } else {
      await loadEmails()
    }

    // 3秒后重置状态为idle
    setTimeout(() => {
      syncStatus.value = 'idle'
      syncProgress.value = 0
    }, 3000)

  } catch (error) {
    console.error('邮件同步失败:', error)
    syncStatus.value = 'error'
    syncing.value = false
    lastSyncResult.value = null // 清空同步结果

    if (!isAutoSync) {
      // 优先显示 detail 字段的详细错误信息
      const errorMessage = error.detail || error.error || error.message || '邮件同步失败'
      toast.error(errorMessage)
    }
  }
}

// 同步单个账户（从账户列表项头部的同步按钮触发）
const syncOneAccount = async (account) => {
  if (syncing.value) return // 防止并发同步
  syncingAccountId.value = account.id
  try {
    await syncEmails(false, account)
  } finally {
    syncingAccountId.value = null
  }
}

const clearSelection = async () => {
  if (clearingSelection.value) return // 防止重复点击
  
  try {
    clearingSelection.value = true
    
    // 清空选中状态
    selectedEmail.value = null
    selectedFolder.value = folders.value.find(f => f.folder_type === 'all') || folders.value[0]
    selectedAccount.value = 'all'
    selectedAccountFolder.value = null
    
    // 重置搜索
    searchQuery.value = ''
    
    // 刷新聚合统计为全部账户的统计
    await refreshFolderStats('all')
    
    // 重新加载数据
    await loadEmails()
    
    toast.success('已清空选择')
  } catch (error) {
    console.error('清空选择失败:', error)
    toast.error('操作失败')
  } finally {
    clearingSelection.value = false
  }
}

const selectFolder = async (folder) => {
  if (loadingEmails.value || loadingFolderSelection.value) return
  
  try {
    loadingFolderSelection.value = true
    loadingFolderId.value = folder.id
    
    selectedFolder.value = folder
    selectedAccountFolder.value = null
    currentPage.value = 1
    
    // 如果有搜索关键词，则在新范围内搜索；否则正常加载
    if (searchQuery.value.trim()) {
      await performSearch()
    } else {
      await loadEmails()
    }
  } catch (error) {
    console.error('选择文件夹失败:', error)
    toast.error('切换文件夹失败')
  } finally {
    loadingFolderSelection.value = false
    loadingFolderId.value = null
  }
}

const selectAccountFolder = async (account, folder) => {
  // 为用户创建的文件夹使用唯一ID，避免loading状态冲突
  const uniqueLoadingId = folder.folder_type === 'user_created' 
    ? `${account.id}-${folder.folder_type}-${folder.id}` 
    : `${account.id}-${folder.folder_type}`
  
  // 防止重复点击
  if (loadingAccountSelection.value && loadingAccountId.value === uniqueLoadingId) return
  
  try {
    loadingAccountSelection.value = true
    loadingAccountId.value = uniqueLoadingId
    
    
    // 设置选中状态
    selectedAccount.value = account  // 设置为账户对象，不是ID
    selectedAccountFolder.value = folder
    selectedFolder.value = null
    currentPage.value = 1
    
    // 刷新聚合统计为该账户的统计
    await refreshFolderStats(account.id)
    
    // 根据文件夹类型构建查询参数
    const searchParams = { account: account.id }
    
    // 处理不同的文件夹类型
    if (folder.folder_type === 'unread') {
      // 未读邮件：使用is_read=false过滤
      searchParams.is_read = false
    } else if (folder.folder_type === 'important') {
      // 重要邮件：使用is_important=true过滤  
      searchParams.is_important = true
    } else if (folder.folder_type === 'user_created') {
      // 用户创建文件夹：查询归档到该文件夹的邮件
      searchParams.archived_folder = folder.id
    } else {
      // 标准文件夹类型：使用folder__folder_type过滤
      searchParams.folder__folder_type = folder.folder_type
    }
    
    // 如果有搜索关键词，则在新范围内搜索；否则正常加载
    if (searchQuery.value.trim()) {
      await performSearch()
    } else {
      await loadEmails(searchParams)
    }
    
  } catch (error) {
    console.error('选择账户文件夹失败:', error)
    toast.error('切换文件夹失败，请重试')
  } finally {
    loadingAccountSelection.value = false
    loadingAccountId.value = null
  }
}

const toggleAccount = (account) => {
  account.expanded = !account.expanded
}

const addNewFolder = async (account) => {
  try {
    const folderName = prompt('请输入文件夹名称：')
    if (!folderName || !folderName.trim()) return
    
    const folderData = {
      account: account.id,
      name: folderName.trim(),
      folder_type: 'user_created'
    }
    
    const result = await emailServiceV2.createFolder(folderData)
    if (result.success) {
      // 添加到本地状态
      account.folders.push({
        ...result.data,
        count: 0,
        icon: FolderIcon
      })
      
      toast.success('文件夹创建成功')
    }
  } catch (error) {
    console.error('创建文件夹失败:', error)
    toast.error(error.error || '创建文件夹失败')
  }
}

const deleteFolder = async (account, folder) => {
  if (!confirm(`确定要删除文件夹"${folder.name}"吗？此操作不可撤销。`)) return
  
  try {
    deletingFolderId.value = folder.id
    await emailServiceV2.deleteFolder(folder.id)
    
    // 从本地状态中删除
    const folderIndex = account.folders.findIndex(f => f.id === folder.id)
    if (folderIndex !== -1) {
      account.folders.splice(folderIndex, 1)
    }
    
    // 如果删除的是当前选中的文件夹，清除选中状态
    if (selectedAccountFolder.value?.id === folder.id) {
      selectedAccountFolder.value = null
    }
    
    toast.success('文件夹删除成功')
  } catch (error) {
    console.error('删除文件夹失败:', error)
    toast.error(error.error || '删除文件夹失败')
  } finally {
    deletingFolderId.value = null
  }
}

// 账户选择方法
const selectAccount = async (account) => {
  // 防止重复点击
  if (loadingAccountSelection.value && loadingAccountId.value === account.id) return
  
  try {
    loadingAccountSelection.value = true
    loadingAccountId.value = account.id
    
    
    // 更新选中的账户
    selectedAccount.value = account
    
    // 清除邮件选择和账户文件夹选择
    selectedEmail.value = null
    selectedAccountFolder.value = null
    
    // 设置聚合文件夹为"全部邮件"
    selectedFolder.value = folders.value.find(folder => folder.folder_type === 'all') || folders.value[0]
    
    // 重置分页
    currentPage.value = 1
    
    // 重新获取该账户的聚合统计
    await refreshFolderStats(account.id)
    
    // 如果有搜索关键词，则在新范围内搜索；否则正常加载邮件列表（按账户过滤）
    if (searchQuery.value.trim()) {
      await performSearch()
    } else {
      await loadEmails()
    }
    
  } catch (error) {
    console.error('切换账户失败:', error)
    toast.error('切换账户失败，请重试')
  } finally {
    loadingAccountSelection.value = false
    loadingAccountId.value = null
  }
}

// 切换到全部账户
const selectAllAccounts = async () => {
  // 防止重复点击
  if (loadingAccountSelection.value && loadingAccountId.value === 'all') return
  
  try {
    loadingAccountSelection.value = true
    loadingAccountId.value = 'all'
    
    
    // 更新选中的账户为'all'
    selectedAccount.value = 'all'
    
    // 清除邮件选择和账户文件夹选择
    selectedEmail.value = null
    selectedAccountFolder.value = null
    
    // 设置聚合文件夹为"全部邮件"
    selectedFolder.value = folders.value.find(folder => folder.folder_type === 'all') || folders.value[0]
    
    // 重置分页
    currentPage.value = 1
    
    // 重新获取全部账户的聚合统计
    await refreshFolderStats('all')
    
    // 如果有搜索关键词，则在新范围内搜索；否则正常加载邮件列表（不过滤账户）
    if (searchQuery.value.trim()) {
      await performSearch()
    } else {
      await loadEmails()
    }
    
  } catch (error) {
    console.error('切换到全部账户失败:', error)
    toast.error('切换账户失败，请重试')
  } finally {
    loadingAccountSelection.value = false
    loadingAccountId.value = null
  }
}

const performSearch = async (page = 1) => {
  if (!searchQuery.value.trim()) {
    // 如果搜索为空，重置分页并加载正常邮件列表
    currentPage.value = 1
    await loadEmails()
    return
  }
  
  try {
    // 🎯 开启加载状态 - 显示骨架加载
    loadingEmails.value = true
    
    // 如果没有指定页码，则重置到第1页；否则使用指定页码
    if (page === 1) {
      currentPage.value = 1
    } else {
      currentPage.value = page
    }
    
    // 构建搜索选项，考虑当前筛选状态
    const searchOptions = {
      page: currentPage.value,
      page_size: pageSize.value,
      order_by: '-received_date'
    }
    
    // 优先处理账户文件夹（selectedAccountFolder）
    if (selectedAccountFolder.value) {
      const folder = selectedAccountFolder.value
      const account = selectedAccount.value
      
      // 设置账户筛选
      if (account && account !== 'all') {
        searchOptions.account_ids = [account.id || account]
      }
      
      // 根据文件夹类型设置过滤参数
      if (folder.folder_type === 'unread') {
        searchOptions.is_read = false
      } else if (folder.folder_type === 'important') {
        searchOptions.is_important = true
      } else if (folder.folder_type === 'user_created') {
        searchOptions.archived_folder = folder.id
      } else {
        // 使用 folder_types 数组参数来限制文件夹范围
        searchOptions.folder_types = [folder.folder_type]
      }
    }
    // 否则处理聚合文件夹（selectedFolder）
    else if (selectedFolder.value && selectedFolder.value.folder_type) {
      const folderType = selectedFolder.value.folder_type
      
      // 如果选中了特定账户
      if (selectedAccount.value && selectedAccount.value !== 'all') {
        searchOptions.account_ids = [selectedAccount.value.id || selectedAccount.value]
      }
      
      // 根据聚合文件夹类型设置筛选参数
      switch (folderType) {
        case 'all':
          // 全部邮件 - 不添加额外过滤条件
          break
        case 'inbox':
          searchOptions.folder_types = ['inbox']
          break
        case 'sent':
          searchOptions.folder_types = ['sent']
          break
        case 'unread':
          searchOptions.is_read = false
          break
        case 'important':
          searchOptions.is_important = true
          break
        case 'spam':
          searchOptions.folder_types = ['spam']
          break
        default:
          searchOptions.folder_types = [folderType]
      }
    }
    
    const result = await emailServiceV2.quickSearch(searchQuery.value.trim(), searchOptions)
    if (result.success) {
      emailList.value = result.results
      totalEmails.value = result.count
    }
  } catch (error) {
    console.error('搜索失败:', error)
    toast.error(error.error || '搜索失败')
  } finally {
    // 🎯 关闭加载状态 - 隐藏骨架加载
    loadingEmails.value = false
  }
}

// 监听搜索输入变化
watch(searchQuery, () => {
  if (searchQuery.value.trim()) {
    // 防抖搜索
    clearTimeout(window.searchTimeout)
    window.searchTimeout = setTimeout(performSearch, 500)
  } else {
    // 搜索框清空时，立即恢复到当前筛选状态
    clearTimeout(window.searchTimeout)
    window.searchTimeout = setTimeout(performSearch, 100) // 短延迟确保输入框已更新
  }
})

// 分页方法
const changePage = async (page) => {
  if (page === currentPage.value || loadingEmails.value) return
  
  // 边界检查
  if (page < 1 || page > totalPages.value) return
  
  // 如果当前在搜索状态，使用搜索分页；否则使用普通分页
  if (searchQuery.value.trim()) {
    await performSearch(page)
  } else {
    currentPage.value = page
    await loadEmails()
  }
}

// 跳转到指定页码
const handleJumpToPage = async () => {
  if (loadingEmails.value) return
  
  const page = parseInt(jumpToPage.value)
  if (isNaN(page) || page < 1 || page > totalPages.value) {
    toast.error(`请输入有效页码 (1-${totalPages.value})`)
    return
  }
  
  await changePage(page)
  jumpToPage.value = null
}

// 处理每页数量变化
const handlePageSizeChange = async () => {
  if (loadingEmails.value) return

  pageSize.value = Number(pageSize.value)
  localStorage.setItem(EMAIL_LIST_PAGE_SIZE_KEY, String(pageSize.value))
  currentPage.value = 1

  if (searchQuery.value.trim()) {
    await performSearch(1)
  } else {
    await loadEmails()
  }
}

// 刷新文件夹统计信息
const refreshFolderStats = async (accountId = null) => {
  try {
    const params = {}
    if (accountId && accountId !== 'all') {
      params.account_id = accountId
    }
    
    const folderStatsResult = await emailServiceV2.getAggregatedFolderStats(params)
    
    // 更新聚合文件夹的计数
    folders.value.forEach(folder => {
      if (folderStatsResult[folder.folder_type] !== undefined) {
        folder.count = folderStatsResult[folder.folder_type]
      }
    })
  } catch (error) {
    console.error('刷新文件夹统计失败:', error)
  }
}

// 刷新特定账户的文件夹统计
const refreshAccountFolderStats = async (emailAccountId) => {
  try {
    if (!emailAccountId) return
    
    
    // 找到对应的账户
    const account = emailAccounts.value.find(acc => acc.id === emailAccountId)
    if (!account || !account.folders) {
      console.log(`⚠️ [DEBUG] 未找到账户 ${emailAccountId} 或其文件夹`)
      return
    }
    
    // 1. 获取该账户的聚合统计（标准文件夹）
    const accountFolderStats = await emailServiceV2.getAggregatedFolderStats({ account_id: emailAccountId })
    
    // 2. 获取该账户的详细文件夹统计（包括用户创建的文件夹）
    const detailedFolderStats = await emailServiceV2.getFoldersWithStats(emailAccountId)
    
    // 更新账户下每个文件夹的统计
    account.folders.forEach(folder => {
      const folderType = folder.folder_type
      
      // 对于标准文件夹，使用聚合统计
      if (accountFolderStats[folderType] !== undefined) {
        const oldCount = folder.count
        folder.count = accountFolderStats[folderType]
      }
      // 对于用户创建的文件夹，使用详细统计
      else if (folderType === 'user_created' && detailedFolderStats) {
        // detailedFolderStats 是一个数组，查找匹配的文件夹
        const detailedFolder = detailedFolderStats.find(f => f.id === folder.id && f.folder_type === 'user_created')
        if (detailedFolder) {
          const oldCount = folder.count
          const oldUnreadCount = folder.unread_count
          folder.count = detailedFolder.total_emails || 0
          folder.unread_count = detailedFolder.unread_emails || 0
        }
      }
    })
    
    // 🔥 更新账户级别的未读邮件数（使用聚合统计，避免重复计算）
    // accountFolderStats.unread 是后端已经去重的未读邮件总数
    account.unread_count = accountFolderStats.unread || 0
    
  } catch (error) {
    console.error('刷新账户文件夹统计失败:', error)
  }
}

// 历史邮件数据直接从 selectedEmail.related_emails 获取
const historyEmails = computed(() => {
  return selectedEmail.value?.related_emails || []
})

// 加载历史邮件内容
const loadHistoryEmail = async () => {
  try {
    if (!selectedHistoryEmail.value) {
      // 如果选择了空值，不做任何操作
      return
    }
    
    // 调用loadEmailContent来加载选中的历史邮件
    await loadEmailContent(selectedHistoryEmail.value)
    
    // 重置历史邮件选择（可选，让用户知道已经切换了）
    selectedHistoryEmail.value = ''
    
  } catch (error) {
    console.error('加载历史邮件失败:', error)
    toast.error('加载历史邮件失败，请重试')
  }
}

// 客户信息数据  
const selectedHistoryEmail = ref('')

// 监听选中邮件变化，自动搜索客户信息
watch(selectedEmail, async (newEmail, oldEmail) => {
  // 🔧 防止重复加载：只有当邮件ID真正改变时才查询客户信息
  if (newEmail && newEmail.id && (!oldEmail || oldEmail.id !== newEmail.id)) {
    try {
      loadingCustomer.value = true
      
      // 🔧 使用新的API - 传递邮件ID而不是邮箱地址，解决脱敏邮箱问题
      const result = await customerService.searchCustomerByEmailId(newEmail.id)
      
      if (result.success) {
        if (result.data && result.meta.has_customer) {
          // 现有客户 - 使用API返回的脱敏邮箱地址
          fillCustomerInfo(result.data, result.meta.display_email)
          // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
        } else {
          // 新客户 - 使用API返回的脱敏邮箱地址
          fillCustomerInfo(null, result.meta.display_email)
          // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
        }
      } else {
        console.warn('新API搜索失败，降级到原有方式:', result.message)
        // 降级处理：使用原有方式
        const customerEmail = getCustomerEmail(newEmail)
        if (customerEmail) {
          const customer = await searchCustomerByEmail(customerEmail)
          fillCustomerInfo(customer, customerEmail)
          // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
        } else {
          fillCustomerInfo(null, '')
          // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
        }
      }
    } catch (error) {
      console.error('搜索客户失败，降级到原有方式:', error)
      // 降级处理：使用原有方式
      const customerEmail = getCustomerEmail(newEmail)
      if (customerEmail) {
        const customer = await searchCustomerByEmail(customerEmail)
        fillCustomerInfo(customer, customerEmail)
        // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
      } else {
        fillCustomerInfo(null, '')
        // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
      }
    } finally {
      loadingCustomer.value = false
    }
  } else if (!newEmail) {
    // 清空客户信息
    fillCustomerInfo(null, '')
    // customerExists 是计算属性，会根据 customerInfo.value.id 自动更新
  }
})

// 页面生命周期
onMounted(async () => {
  // 预加载系统配置
  preloadConfig()
  
  // 🚀 使用新的初始化 API，一次性获取所有数据（包括标签和分组）
  await initializeData()
})

// 组件销毁时清理资源
onUnmounted(() => {
  // 清除产品搜索定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  
  // 清除自动同步定时器（历史遗留，自动同步已改为后端 Celery 负责）
  if (autoSyncInterval.value) {
    clearInterval(autoSyncInterval.value)
    autoSyncInterval.value = null
  }

})

// 客户信息相关
const customerInfo = ref({
  id: null, // 客户ID，用于判断是编辑还是新建
  email: '',
  ownedEmailAccount: '',
  firstName: '',
  lastName: '',
  phone: '',
  source: '',
  orderNumber: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  address: '',
  notes: '',
  tagIds: [],
  groupIds: [],
  productIds: []
})

const loadingCustomer = ref(false)
const savingCustomer = ref(false)

// 真实客户相关数据
const availableTags = ref([])
const availableGroups = ref([])
const loadingTags = ref(false)
const loadingGroups = ref(false)

// 初始加载时的选中状态（用于排序，保持位置稳定）
const initialSelectedTagIds = ref([])
const initialSelectedGroupIds = ref([])

// 移除模拟产品数据，使用真实API

// 产品绑定相关状态
const boundProducts = ref([])
const productSearchQuery = ref('')
const productSearchResults = ref([])
const isSearchingProducts = ref(false)
let searchTimeout = null // 防抖定时器

// 标签和分组搜索
const tagSearchQuery = ref('')
const groupSearchQuery = ref('')

// 获取客户邮箱地址（根据邮件类型判断）
const getCustomerEmail = (email) => {
  if (!email) return ''
  
  const folderType = email.folder_type || 'inbox'
  
  // 根据文件夹类型判断客户邮箱
  if (folderType === 'sent') {
    // 已发送邮件：收件人是客户
    // 取第一个收件人作为客户邮箱
    return email.recipient_emails && email.recipient_emails.length > 0 ? email.recipient_emails[0] : ''
  } else {
    // 收件箱、垃圾邮件等：发件人是客户
    return email.sender_email || ''
  }
}

// 获取邮件列表中应该显示的联系人信息（根据文件夹类型判断）
const getDisplayContactInfo = (email) => {
  if (!email) return { name: '', email: '' }
  
  const folderType = email.folder_type || 'inbox'
  
  if (folderType === 'sent') {
    // 已发送邮件：直接使用 recipient_list（后端已格式化）
    return {
      name: '',
      email: '',
      recipientList: email.recipient_list || ''
    }
  } else {
    // 收件箱、垃圾邮件等：显示发件人信息
    return {
      name: email.sender_name || '',
      email: email.sender_email || ''
    }
  }
}

// 获取联系人显示文本（仅联系人信息，不包含文件夹）
const getContactDisplayText = (email) => {
  const contact = getDisplayContactInfo(email)
  
  // 已发送邮件：直接显示 recipient_list
  if (contact.recipientList) {
    return contact.recipientList
  }
  
  // 其他邮件：显示发件人信息
  if (contact.name) {
    return `${contact.name} (${contact.email})`
  } else {
    return contact.email
  }
}

// 获取邮件列表显示文本（联系人信息 + 文件夹名称）- 保留向后兼容
const getEmailDisplayText = (email) => {
  const contactText = getContactDisplayText(email)
  const folderName = translateFolderName(email.folder_name)
  
  return `${contactText} - ${folderName}`
}

// 回复邮件相关方法
const openReplyModal = (email) => {
  replyTargetEmail.value = email
  showReplyModal.value = true
}

const closeReplyModal = () => {
  showReplyModal.value = false
  replyTargetEmail.value = null
}

// 新建邮件相关方法
const openComposeModal = () => {
  defaultFromAccountId.value = null  // 通用新建邮件，不设置默认账户
  showComposeModal.value = true
}

const openComposeModalForAccount = (account) => {
  defaultFromAccountId.value = account.id  // 设置默认发件账户
  showComposeModal.value = true
}

const closeComposeModal = () => {
  showComposeModal.value = false
  defaultFromAccountId.value = null  // 清空默认账户
}

// 备注相关方法
const openNotesModal = (account) => {
  currentNotesAccount.value = account
  accountNotes.value = '' // 每次打开都清空内容
  showNotesModal.value = true
}

const closeNotesModal = () => {
  showNotesModal.value = false
  currentNotesAccount.value = null
  accountNotes.value = ''
}

const saveNotes = async () => {
  if (!currentNotesAccount.value) return
  
  try {
    savingNotes.value = true
    
    // 调用后端API保存备注
    const response = await apiRequest.patch(`/api/email-accounts/v2/accounts/${currentNotesAccount.value.id}/`, {
      notes: accountNotes.value
    })
    
    if (response.data) {
      // 更新本地账户数据
      const accountIndex = emailAccounts.value.findIndex(acc => acc.id === currentNotesAccount.value.id)
      if (accountIndex !== -1) {
        emailAccounts.value[accountIndex].notes = accountNotes.value
      }
      
      toast.success('备注保存成功')
      closeNotesModal()
    }
  } catch (error) {
    console.error('保存备注失败:', error)
    toast.error('保存备注失败，请重试')
  } finally {
    savingNotes.value = false
  }
}

const onEmailSent = () => {
  // 邮件发送成功后的回调
  // 成功提示由EmailReplyModal组件负责显示，这里只处理数据刷新
  // 可以选择刷新邮件列表
  loadEmails()
}

// 历史往来相关函数
const openHistoryModal = async () => {
  if (!selectedEmail.value) {
    toast.warning('请先选择一封邮件')
    return
  }
  showHistoryModal.value = true
  historyActiveTab.value = 'all'
  
  // 并行加载所有Tab的count和当前Tab的邮件列表
  await Promise.all([
    loadAllTabCounts(),
    loadHistoryEmails('all', 1)
  ])
}

const closeHistoryModal = () => {
  showHistoryModal.value = false
  historyEmailList.value = []
  historySelectedEmail.value = null
  historyActiveTab.value = 'all'
  historyPagination.value = {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  }
}

const switchHistoryTab = (tab) => {
  historyActiveTab.value = tab
  loadHistoryEmails(tab, 1)
}

const loadHistoryEmails = async (tab, page) => {
  if (!selectedEmail.value) return
  
  try {
    loadingHistory.value = true
    const response = await apiRequest.get(`/api/emails/v2/emails/${selectedEmail.value.id}/related/`, {
      params: {
        type: tab,
        page: page,
        page_size: 20
      }
    })
    
    historyEmailList.value = response.data.results
    historyPagination.value = {
      currentPage: response.data.current_page,
      pageSize: response.data.page_size,
      totalCount: response.data.count,
      totalPages: response.data.total_pages
    }
    
    // 更新当前Tab的count
    historyTabCounts.value[tab] = response.data.count
    
    // 默认选中第一封邮件
    if (historyEmailList.value.length > 0) {
      selectHistoryEmail(historyEmailList.value[0])
    } else {
      historySelectedEmail.value = null
    }
  } catch (error) {
    console.error('加载历史邮件失败:', error)
    toast.error('加载历史邮件失败，请重试')
  } finally {
    loadingHistory.value = false
  }
}

// 加载所有Tab的count
const loadAllTabCounts = async () => {
  if (!selectedEmail.value) return
  
  try {
    // 并行加载三个Tab的count
    const [allRes, receivedRes, sentRes] = await Promise.all([
      apiRequest.get(`/api/emails/v2/emails/${selectedEmail.value.id}/related/`, {
        params: { type: 'all', page: 1, page_size: 1 }
      }),
      apiRequest.get(`/api/emails/v2/emails/${selectedEmail.value.id}/related/`, {
        params: { type: 'received', page: 1, page_size: 1 }
      }),
      apiRequest.get(`/api/emails/v2/emails/${selectedEmail.value.id}/related/`, {
        params: { type: 'sent', page: 1, page_size: 1 }
      })
    ])
    
    historyTabCounts.value = {
      all: allRes.data.count,
      received: receivedRes.data.count,
      sent: sentRes.data.count
    }
  } catch (error) {
    console.error('加载Tab统计失败:', error)
  }
}

const selectHistoryEmail = async (email) => {
  // 先清空当前选中，显示加载状态
  loadingHistoryDetail.value = true
  historySelectedEmail.value = null
  
  // 加载完整邮件内容
  await loadHistoryEmailDetail(email.id)
  loadingHistoryDetail.value = false
  
  // 如果邮件未读，自动标记为已读
  if (historySelectedEmail.value && !historySelectedEmail.value.is_read) {
    await markHistoryEmailAsRead(historySelectedEmail.value)
  }
}

const loadHistoryEmailDetail = async (emailId) => {
  try {
    const response = await apiRequest.get(`/api/emails/v2/emails/${emailId}/`)
    if (response.data) {
      historySelectedEmail.value = response.data
    }
  } catch (error) {
    console.error('加载邮件详情失败:', error)
    toast.error('加载邮件详情失败')
  }
}

const markHistoryEmailAsRead = async (email) => {
  try {
    await emailServiceV2.markEmailRead(email.id, true)
    
    // 更新历史列表中的邮件状态
    const emailInHistoryList = historyEmailList.value.find(e => e.id === email.id)
    if (emailInHistoryList) {
      emailInHistoryList.is_read = true
    }
    
    // 更新当前选中的历史邮件状态
    if (historySelectedEmail.value && historySelectedEmail.value.id === email.id) {
      historySelectedEmail.value.is_read = true
    }
    
    // 更新主邮件列表中的状态（如果存在）
    const emailInMainList = emailList.value.find(e => e.id === email.id)
    if (emailInMainList) {
      emailInMainList.is_read = true
    }
    
    // 如果是当前选中的主邮件，也更新它
    if (selectedEmail.value && selectedEmail.value.id === email.id) {
      selectedEmail.value.is_read = true
    }
    
    // 更新文件夹统计
    const accountId = selectedAccount.value && selectedAccount.value !== 'all' ? selectedAccount.value.id : null
    await refreshFolderStats(accountId)
    
    // 同时更新邮件所属账户的文件夹统计
    if (email.account) {
      await refreshAccountFolderStats(email.account)
    }
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

const goToHistoryPage = (page) => {
  if (page < 1 || page > historyPagination.value.totalPages) return
  loadHistoryEmails(historyActiveTab.value, page)
}

// 根据邮箱地址搜索客户
const searchCustomerByEmail = async (email) => {
  if (!email) return null
  
  try {
    loadingCustomer.value = true
    const result = await customerService.searchCustomerByEmail(email)
    return result.success ? result.data : null
  } catch (error) {
    console.error('搜索客户失败:', error)
    return null
  } finally {
    loadingCustomer.value = false
  }
}

// 填充客户信息
const fillCustomerInfo = (customer, customerEmail = '') => {
  if (customer) {
    // 编辑模式：填充现有客户数据
    customerInfo.value = {
      id: customer.id,
      email: customer.email || '',
      ownedEmailAccount: customer.owned_email_account || '',
      firstName: customer.first_name || '',
      lastName: customer.last_name || '',
      phone: customer.phone || '',
      source: customer.source || '',
      orderNumber: customer.order_number || '',
      country: customer.country || '',
      state: customer.state || '',
      city: customer.city || '',
      postalCode: customer.postal_code || '',
      address: customer.address || '',
      notes: customer.notes || '',
      tagIds: customer.tags ? customer.tags.map(tag => tag.id) : [],
      groupIds: customer.groups ? customer.groups.map(group => group.id) : [],
      productIds: customer.products ? customer.products.map(product => product.id) : []
    }
    
    // 记录初始选中状态（用于排序）
    initialSelectedTagIds.value = customer.tags ? customer.tags.map(tag => tag.id) : []
    initialSelectedGroupIds.value = customer.groups ? customer.groups.map(group => group.id) : []
    
    // 同时更新已绑定产品列表
    boundProducts.value = customer.products ? [...customer.products] : []
  } else {
    // 新建模式：重置表单，使用传入的客户邮箱地址
    // 🔧 自动填入归属邮箱：获取邮件接收账户的邮箱地址
    let ownedEmail = ''
    if (selectedEmail.value && selectedEmail.value.account) {
      const account = emailAccounts.value.find(acc => acc.id === selectedEmail.value.account)
      if (account && account.email) {
        ownedEmail = account.email
      }
    }
    
    customerInfo.value = {
      id: null,
      email: customerEmail,
      ownedEmailAccount: ownedEmail,
      firstName: '',
      lastName: '',
      phone: '',
      source: '邮件导入',
      orderNumber: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      address: '',
      notes: '',
      tagIds: [],
      groupIds: [],
      productIds: []
    }
    
    // 新建客户时清空初始选中状态
    initialSelectedTagIds.value = []
    initialSelectedGroupIds.value = []
    
    // 清空已绑定产品列表
    boundProducts.value = []
  }
}

// 缓存标志：标签和分组是否已加载
const tagsLoaded = ref(false)
const groupsLoaded = ref(false)

// 加载标签数据
const loadCustomerTags = async (force = false) => {
  // 如果已加载且非强制刷新，直接返回
  if (tagsLoaded.value && !force) {
    return
  }
  
  try {
    loadingTags.value = true
    const result = await customerTagService.getTags()
    if (result.success) {
      availableTags.value = result.data.results || result.data || []
      tagsLoaded.value = true
    } else {
      console.error('获取标签失败:', result.message)
      toast.error('获取标签失败')
    }
  } catch (error) {
    console.error('获取标签失败:', error)
    toast.error('获取标签失败')
  } finally {
    loadingTags.value = false
  }
}

// 加载分组数据
const loadCustomerGroups = async (force = false) => {
  // 如果已加载且非强制刷新，直接返回
  if (groupsLoaded.value && !force) {
    return
  }
  
  try {
    loadingGroups.value = true
    const result = await customerGroupService.getGroups()
    if (result.success) {
      availableGroups.value = result.data.results || result.data || []
      groupsLoaded.value = true
    } else {
      console.error('获取分组失败:', result.message)
      toast.error('获取分组失败')
    }
  } catch (error) {
    console.error('获取分组失败:', error)
    toast.error('获取分组失败')
  } finally {
    loadingGroups.value = false
  }
}

// 客户数据检查
const customerExists = computed(() => {
  return customerInfo.value.id !== null
})

// 复制功能
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    toast.error('复制失败')
  }
}

// 解析API错误信息的辅助函数
const parseApiErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return null
  }
  
  const errorMessages = []
  
  // 字段名到中文的映射
  const fieldNames = {
    'email': '邮箱地址',
    'first_name': '名字',
    'last_name': '姓氏',
    'phone': '电话',
    'source': '来源',
    'order_number': '订单号码',
    'country': '国家',
    'state': '省/州',
    'city': '城市',
    'postal_code': '邮政编码',
    'address': '地址',
    'notes': '备注',
    'tag_ids': '标签',
    'group_ids': '客户分组',
    'product_ids': '产品'
  }
  
  // 遍历错误字段
  Object.keys(errors).forEach(fieldKey => {
    const fieldErrors = errors[fieldKey]
    const fieldName = fieldNames[fieldKey] || fieldKey
    
    if (Array.isArray(fieldErrors)) {
      fieldErrors.forEach(error => {
        if (typeof error === 'string') {
          errorMessages.push(`${fieldName}: ${error}`)
        } else if (error && typeof error === 'object' && error.string) {
          errorMessages.push(`${fieldName}: ${error.string}`)
        }
      })
    } else if (typeof fieldErrors === 'string') {
      errorMessages.push(`${fieldName}: ${fieldErrors}`)
    }
  })
  
  return errorMessages.length > 0 ? errorMessages.join('; ') : null
}

// 客户操作方法
const saveCustomerInfo = async () => {
  if (savingCustomer.value) return // 防止重复提交
  
  try {
    savingCustomer.value = true
    
    // 验证必填字段
    if (!customerInfo.value.email) {
      toast.error('请填写邮箱地址')
      savingCustomer.value = false
      return
    }
    
    // 验证是否有选中的邮件（用于获取真实邮箱地址）
    if (!selectedEmail.value || !selectedEmail.value.id) {
      toast.error('请先选择一封邮件')
      savingCustomer.value = false
      return
    }
    
    // 准备客户数据（不包含email，由后端从邮件中提取真实邮箱）
    const customerData = {
      first_name: customerInfo.value.firstName,
      last_name: customerInfo.value.lastName,
      phone: customerInfo.value.phone,
      owned_email_account: customerInfo.value.ownedEmailAccount,
      source: customerInfo.value.source,
      order_number: customerInfo.value.orderNumber,
      country: customerInfo.value.country,
      state: customerInfo.value.state,
      city: customerInfo.value.city,
      postal_code: customerInfo.value.postalCode,
      address: customerInfo.value.address,
      notes: customerInfo.value.notes,
      tag_ids: customerInfo.value.tagIds,
      group_ids: customerInfo.value.groupIds,
      product_ids: customerInfo.value.productIds
    }
    
    // 🔧 使用新的API - 传递邮件ID，后端解析真实邮箱地址
    const result = await customerService.createCustomerFromEmailId(selectedEmail.value.id, customerData)
    
    if (result.success) {
      toast.success('客户创建成功')
      // 使用返回的客户数据更新界面，邮箱地址已脱敏
      const customerData = result.data.customer || result.data
      fillCustomerInfo(customerData, customerData.email)
      customerExists.value = true
    } else {
      console.warn('新API创建失败，降级到原有方式:', result.message)
      
      // 降级处理：使用原有API
      const fallbackData = {
        ...customerData,
        email: customerInfo.value.email // 使用脱敏邮箱地址
      }
      
      const fallbackResult = await customerService.createCustomer(fallbackData)
      
      if (fallbackResult.success) {
        toast.success('客户创建成功')
        // 🔧 降级API返回的数据结构可能不同，适配处理
        const customerData = fallbackResult.data.customer || fallbackResult.data
        fillCustomerInfo(customerData, customerData.email)
        customerExists.value = true
      } else {
        // 尝试解析具体的验证错误
        const detailedError = parseApiErrors(fallbackResult.errors)
        const errorMessage = detailedError || fallbackResult.message || '客户创建失败'
        toast.error(errorMessage)
      }
    }
  } catch (error) {
    console.error('创建客户失败，尝试降级处理:', error)
    
    try {
      // 降级处理：使用原有API
      const fallbackData = {
        email: customerInfo.value.email,
        first_name: customerInfo.value.firstName,
        last_name: customerInfo.value.lastName,
        phone: customerInfo.value.phone,
        owned_email_account: customerInfo.value.ownedEmailAccount,
        source: customerInfo.value.source,
        order_number: customerInfo.value.orderNumber,
        country: customerInfo.value.country,
        state: customerInfo.value.state,
        city: customerInfo.value.city,
        postal_code: customerInfo.value.postalCode,
        address: customerInfo.value.address,
        notes: customerInfo.value.notes,
        tag_ids: customerInfo.value.tagIds,
        group_ids: customerInfo.value.groupIds,
        product_ids: customerInfo.value.productIds
      }
      
      const fallbackResult = await customerService.createCustomer(fallbackData)
      
      if (fallbackResult.success) {
        toast.success('客户创建成功')
        // 🔧 降级API返回的数据结构可能不同，适配处理
        const customerData = fallbackResult.data.customer || fallbackResult.data
        fillCustomerInfo(customerData, customerData.email)
        customerExists.value = true
      } else {
        const detailedError = parseApiErrors(fallbackResult.errors)
        const errorMessage = detailedError || fallbackResult.message || '客户创建失败'
        toast.error(errorMessage)
      }
    } catch (fallbackError) {
      console.error('降级处理也失败:', fallbackError)
      const detailedError = parseApiErrors(fallbackError.response?.data)
      const errorMessage = detailedError || fallbackError.response?.data?.message || '客户创建失败'
      toast.error(errorMessage)
    }
  } finally {
    savingCustomer.value = false
  }
}

const editCustomerInfo = async () => {
  if (savingCustomer.value) return // 防止重复提交
  
  try {
    savingCustomer.value = true
    
    // 验证必填字段
    if (!customerInfo.value.email || !customerInfo.value.id) {
      toast.error('请填写必要信息')
      savingCustomer.value = false
      return
    }
    
    // 准备客户数据
    const customerData = {
      email: customerInfo.value.email,
      first_name: customerInfo.value.firstName,
      last_name: customerInfo.value.lastName,
      phone: customerInfo.value.phone,
      owned_email_account: customerInfo.value.ownedEmailAccount,
      source: customerInfo.value.source,
      order_number: customerInfo.value.orderNumber,
      country: customerInfo.value.country,
      state: customerInfo.value.state,
      city: customerInfo.value.city,
      postal_code: customerInfo.value.postalCode,
      address: customerInfo.value.address,
      notes: customerInfo.value.notes,
      tag_ids: customerInfo.value.tagIds,
      group_ids: customerInfo.value.groupIds,
      product_ids: customerInfo.value.productIds
    }
    
    // 调用API更新客户
    const result = await customerService.updateCustomer(customerInfo.value.id, customerData)
    
    if (result.success) {
      toast.success('客户信息更新成功')
      // 使用返回的完整客户数据更新界面（包括产品信息）
      if (result.data) {
        fillCustomerInfo(result.data, result.data.email)
      }
    } else {
      // 尝试解析具体的验证错误
      const detailedError = parseApiErrors(result.errors)
      const errorMessage = detailedError || result.message || '客户信息更新失败'
      toast.error(errorMessage)
    }
  } catch (error) {
    console.error('更新客户失败:', error)
    
    // 尝试从异常中解析错误信息
    const detailedError = parseApiErrors(error.response?.data)
    const errorMessage = detailedError || error.response?.data?.message || '更新客户失败'
    toast.error(errorMessage)
  } finally {
    savingCustomer.value = false
  }
}

// 产品搜索方法（带防抖）
const searchProducts = (query) => {
  // 清除之前的定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  
  if (!query || query.trim() === '') {
    productSearchResults.value = []
    isSearchingProducts.value = false
    return
  }
  
  // 防抖：延迟执行搜索
  searchTimeout = setTimeout(async () => {
    isSearchingProducts.value = true
    
    try {
      // 使用真实API搜索产品
      const result = await productService.getProductSuggestions(query.trim(), 10)
      
      if (result.success) {
        // 过滤已经绑定的产品和非活跃产品
        const boundProductIds = boundProducts.value.map(p => p.id)
        const filteredProducts = result.data.filter(product => 
          !boundProductIds.includes(product.id) && product.is_active !== false
        )
        
        productSearchResults.value = filteredProducts
      } else {
        productSearchResults.value = []
      }
    } catch (error) {
      console.error('产品搜索失败:', error)
      productSearchResults.value = []
      
      // 只有在非取消错误时才显示错误提示
      if (error.name !== 'CanceledError') {
        toast.error('搜索产品失败，请重试')
      }
    } finally {
      isSearchingProducts.value = false
    }
  }, 300) // 300ms 防抖延迟
}

const addProduct = (product) => {
  if (boundProducts.value.find(p => p.id === product.id)) {
    return
  }
  
  boundProducts.value.push(product)
  customerInfo.value.productIds.push(product.id)
  productSearchResults.value = productSearchResults.value.filter(p => p.id !== product.id)
  
  toast.success(`已添加产品 ${product.name}`)
}

const removeProduct = (product) => {
  boundProducts.value = boundProducts.value.filter(p => p.id !== product.id)
  customerInfo.value.productIds = customerInfo.value.productIds.filter(id => id !== product.id)
  toast.success(`已移除产品 ${product.name}`)
}

// 页面计算属性和监听器
const totalPages = computed(() => Math.ceil(totalEmails.value / pageSize.value))

const paginatedEmails = computed(() => {
  return emailList.value // V2 API已经处理分页
})

// 过滤后的邮件账户列表（排序 + 搜索）
const filteredEmailAccounts = computed(() => {
  const sortedAccounts = sortEmailAccountsList(emailAccounts.value)

  if (!accountSearchQuery.value.trim()) {
    return sortedAccounts
  }

  const query = accountSearchQuery.value.toLowerCase()
  return sortedAccounts.filter(account => {
    const name = (account.name || account.email || '').toLowerCase()
    const email = (account.email || '').toLowerCase()
    const displayName = (account.display_name || '').toLowerCase()

    return name.includes(query) || email.includes(query) || displayName.includes(query)
  })
})

// 过滤后的标签列表（根据搜索关键词，初始选中的排在前面）
const filteredTags = computed(() => {
  let tags = availableTags.value
  
  // 搜索过滤
  if (tagSearchQuery.value.trim()) {
    const query = tagSearchQuery.value.toLowerCase()
    tags = tags.filter(tag => {
      const name = (tag.name || '').toLowerCase()
      return name.includes(query)
    })
  }
  
  // 使用初始选中状态排序（加载时选中的排在前面，后续选择不影响位置）
  const initialIds = initialSelectedTagIds.value || []
  return [...tags].sort((a, b) => {
    const aSelected = initialIds.includes(a.id) ? 0 : 1
    const bSelected = initialIds.includes(b.id) ? 0 : 1
    return aSelected - bSelected
  })
})

// 过滤后的分组列表（根据搜索关键词，初始选中的排在前面）
const filteredGroups = computed(() => {
  let groups = availableGroups.value
  
  // 搜索过滤
  if (groupSearchQuery.value.trim()) {
    const query = groupSearchQuery.value.toLowerCase()
    groups = groups.filter(group => {
      const name = (group.name || '').toLowerCase()
      const description = (group.description || '').toLowerCase()
      return name.includes(query) || description.includes(query)
    })
  }
  
  // 使用初始选中状态排序（加载时选中的排在前面，后续选择不影响位置）
  const initialIds = initialSelectedGroupIds.value || []
  return [...groups].sort((a, b) => {
    const aSelected = initialIds.includes(a.id) ? 0 : 1
    const bSelected = initialIds.includes(b.id) ? 0 : 1
    return aSelected - bSelected
  })
})

// 非内联附件（过滤掉内联附件）
const nonInlineAttachments = computed(() => {
  if (!selectedEmail.value?.attachments) return []
  return selectedEmail.value.attachments.filter(att => !att.is_inline)
})

// 邮件详情显示相关
const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const emailDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  
  if (emailDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  } else {
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
  }
}

const formatDateTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

const formatFileSize = (sizeInBytes) => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`
  } else if (sizeInBytes < 1024 * 1024) {
    return `${Math.round(sizeInBytes / 1024)} KB`
  } else if (sizeInBytes < 1024 * 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`
  } else {
    return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }
}

// 基于 content_type 获取文件类型
const getFileTypeFromContentType = (contentType) => {
  if (!contentType) return 'unknown'
  
  const lowerContentType = contentType.toLowerCase()
  
  // 图片类型
  if (lowerContentType.startsWith('image/')) return 'image'
  
  // PDF类型
  if (lowerContentType === 'application/pdf') return 'pdf'
  
  // Office文档类型
  if (lowerContentType.includes('msword') || 
      lowerContentType.includes('wordprocessingml')) return 'word'
  
  if (lowerContentType.includes('excel') || 
      lowerContentType.includes('spreadsheetml')) return 'excel'
  
  if (lowerContentType.includes('powerpoint') || 
      lowerContentType.includes('presentationml')) return 'powerpoint'
  
  // 文本类型
  if (lowerContentType.startsWith('text/')) return 'text'
  
  // 视频类型
  if (lowerContentType.startsWith('video/')) return 'video'
  
  // 音频类型
  if (lowerContentType.startsWith('audio/')) return 'audio'
  
  // 压缩文件
  if (lowerContentType.includes('zip') || 
      lowerContentType.includes('rar') || 
      lowerContentType.includes('7z') ||
      lowerContentType.includes('tar') ||
      lowerContentType.includes('gz')) return 'archive'
  
  // 代码文件
  if (lowerContentType.includes('javascript') ||
      lowerContentType.includes('json') ||
      lowerContentType.includes('xml') ||
      lowerContentType.includes('html') ||
      lowerContentType.includes('css')) return 'code'
  
  return 'unknown'
}

// 获取文件类型显示名称
const getFileTypeDisplayName = (contentType) => {
  const fileType = getFileTypeFromContentType(contentType)
  
  const nameMap = {
    image: '图片',
    video: '视频',
    audio: '音频', 
    archive: '压缩文件',
    code: '代码文件',
    pdf: 'PDF',
    word: 'Word文档',
    excel: 'Excel表格',
    powerpoint: 'PowerPoint',
    text: '文本文件',
    unknown: '文件'
  }
  
  return nameMap[fileType] || '文件'
}

// 判断文件是否可以预览
const canPreview = (contentType) => {
  if (!contentType) return false
  
  const fileType = getFileTypeFromContentType(contentType)
  
  // 支持预览的文件类型
  const previewableTypes = ['image', 'pdf', 'text', 'video', 'audio']
  return previewableTypes.includes(fileType)
}

// 预览文件
const previewFile = (attachment) => {
  previewedFile.value = attachment
  showFilePreview.value = true
}

// 关闭文件预览
const closeFilePreview = () => {
  showFilePreview.value = false
  previewedFile.value = null
}

// 预览时首次 resolve 到 MinIO 直链后，更新本地附件对象
const onAttachmentUrlResolved = ({ id, url }) => {
  if (previewedFile.value?.id === id) {
    previewedFile.value = { ...previewedFile.value, download_url: url, preview_url: url }
  }
  if (selectedEmail.value?.attachments?.length) {
    const att = selectedEmail.value.attachments.find(a => a.id === id)
    if (att) {
      att.download_url = url
      att.preview_url = url
    }
  }
}

// 切换文件夹列表折叠状态
const toggleFolderList = () => {
  folderListCollapsed.value = !folderListCollapsed.value
  // 保存到 localStorage
  localStorage.setItem('email-folder-list-collapsed', folderListCollapsed.value.toString())
}

// 获取API基础URL（从配置服务）
const getApiBaseUrl = () => {
  return getApiBaseUrlSync()
}

// 下载附件
const downloadAttachment = async (attachment) => {
  // 检查是否已在下载中
  if (downloadingAttachments.value.has(attachment.id)) {
    return
  }
  
  try {
    // 设置下载状态
    downloadingAttachments.value.add(attachment.id)
    
    // 构建下载路径（相对路径，apiRequest会自动处理基础URL）
    let downloadPath = attachment.download_url || ''
    
    if (!isPublicObjectStorageUrl(downloadPath)) {
      const resolveResp = await apiRequest.get(toApiRelativePath(downloadPath) || `/api/emails/v2/attachments/${attachment.id}/download/`, {
        params: { resolve_url: 'true' },
      })
      if (resolveResp.data?.direct_url) {
        downloadPath = resolveResp.data.direct_url
        attachment.download_url = downloadPath
      }
    }
    
    if (isPublicObjectStorageUrl(downloadPath)) {
      const link = document.createElement('a')
      link.href = downloadPath
      link.download = attachment.filename || attachment.name || 'attachment'
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success(`下载成功: ${attachment.filename || attachment.name}`)
      return
    }

    // 使用apiRequest发起带认证的请求（文件仍在 API 代理中）
    const response = await apiRequest.get(
      toApiRelativePath(downloadPath) || `/api/emails/v2/attachments/${attachment.id}/download/`,
      {
      params: { download: 'true' },
      responseType: 'blob' // 重要：指定响应类型为blob
    })
    
    // 获取文件内容
    const blob = response.data
    
    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.filename || attachment.name || 'attachment'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // 清理URL对象
    window.URL.revokeObjectURL(url)
    
    toast.success(`下载成功: ${attachment.filename || attachment.name}`)
  } catch (error) {
    console.error('下载附件失败:', error)
    toast.error(`下载失败: ${error.message || '请重试'}`)
  } finally {
    // 清除下载状态
    downloadingAttachments.value.delete(attachment.id)
  }
}

</script>

<style>
/* 自定义滚动条样式 */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E0 #F7FAFC;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #F7FAFC;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #CBD5E0;
  border-radius: 3px;
  transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #A0AEC0;
}

.custom-scrollbar::-webkit-scrollbar-corner {
  background: #F7FAFC;
}
</style>
